import { uniq } from "lodash";
import moment from "moment";
import { doneAtList, routeList } from "./dropList";
import { billingTabEnum } from "./enum";

export const getDateWithFormat = (date) => {
  const day = date.split("-").shift();
  const month = date.substring(3, 5);
  const year = date.split("-").pop();
  const monStr = moment(new Date(`${month}/${day}/${year}`)).format("MMM");
  return `${monStr} ${day}, ${year}`;
};

export const getTimeDiff = (t2, t1) => {
  const tdiff_in_ms = t2 - t1;
  const tdiff_in_sec = tdiff_in_ms / 1000;
  const tdiff_in_min = tdiff_in_sec / 60;
  const tdiff_in_hours = tdiff_in_min / 60;
  return {
    hh: tdiff_in_hours,
    mm: tdiff_in_min,
  };
};

export const getExpiryTime = (expiryTime) => {
  const currentTime = new Date();
  const expiryTimeInMs = expiryTime.getTime();
  expiryTime.setTime(expiryTimeInMs);
  const tdiff = getTimeDiff(expiryTime.getTime(), currentTime.getTime());
  const expiresIn = {
    hh: Math.floor(tdiff.hh),
    mm: Math.floor(tdiff.mm % 60),
    ss: 0,
  };
  if (expiresIn.mm <= 0) return "Expired";
  return `${expiresIn.hh}h ${expiresIn.mm}m`;
};

export const disableBtn = (date, minutes = 15) => {
  const today = new Date();
  const dbDate = new Date(date);
  if (dbDate.getTime() < today.getTime()) return false;
  if (dbDate.getTime() > today.getTime()) {
    if (
      dbDate.getFullYear() === today.getFullYear() &&
      dbDate.getMonth() === today.getMonth() &&
      dbDate.getDate() === today.getDate()
    ) {
      if (dbDate.getHours() >= today.getHours()) {
        const minDiff = Math.floor(Math.abs(dbDate - today) / 1000 / 60);
        if (minDiff <= minutes) return false;
        return true;
      }
      return true;
    }
    return true;
  }
  return false;
};

export const getDateList = (numberOfDays = 7) => {
  const today = new Date();
  return Array.from({ length: numberOfDays }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return date.toISOString().split("T")[0];
  });
};

export function createTimeSlots(startTime, endTime, duration) {
  // Helper function to pad single digit numbers with a leading zero
  function pad(num) {
    return num < 10 ? "0" + num : num;
  }

  // Helper function to format a Date object to HH:mm string
  function formatTime(date) {
    return pad(date.getHours()) + ":" + pad(date.getMinutes());
  }

  // Parse the start and end times
  const startParts = startTime?.split(":")?.map(Number);
  const endParts = endTime?.split(":")?.map(Number);

  // Create Date objects for the start and end times
  const start = new Date();
  start.setHours(startParts[0], startParts[1], 0, 0);

  const end = new Date();
  end.setHours(endParts[0], endParts[1], 0, 0);

  // Calculate the duration in milliseconds
  const durationMs = duration * 60 * 1000;

  // Generate the time slots
  const slots = [];
  let current = new Date(start);
  while (current <= end) {
    slots.push(formatTime(current));
    current = new Date(current.getTime() + durationMs);
  }

  return slots;
}

export function isTimeBetween(time, startTime) {
  if (!time || !startTime) return false;
  const [startHour, startMinute] = startTime?.split(":").map(Number);
  const [hour, minute] = time?.split(":").map(Number);

  const start = startHour * 60 + startMinute;
  const end = startHour * 60 + 59;
  const date = hour * 60 + minute;

  if (
    (start <= date && date <= end) ||
    date === start ||
    (startHour === hour && startMinute === minute) ||
    time === startTime
  ) {
    return true;
  }
  return false;
}

export const getBorderColor = (status) => {
  switch (status) {
    case "paid" || "Paid":
      return "#80AAD3";
    case "unpaid" || "UnPaid" || "Unpaid":
      return "#ff754a";
    default:
      return "#ff754a";
    // return "transparent";
  }
};

export function splitTimesByInterval(
  startDate,
  startTime,
  endDate,
  endTime,
  numIntervals,
) {
  // Ensure date strings are in YYYY-MM-DD format
  const startDateFormatted = new Date(`${startDate}T${startTime}:00`);
  const endDateFormatted = new Date(`${endDate}T${endTime}:00`);

  // Calculate the total duration in milliseconds
  const totalDuration =
    endDateFormatted.getTime() - startDateFormatted.getTime();

  // Check for invalid input (negative duration)
  if (totalDuration <= 0) {
    throw new Error(
      "Invalid input: Start date/time must be before end date/time",
    );
  }

  // Calculate the interval length in milliseconds, considering potential remainder
  const intervalLength = Math.floor(totalDuration / numIntervals);
  const remainder = totalDuration % numIntervals;

  const intervals = [];

  // Loop through intervals, distributing remainder evenly
  for (let i = 0; i < numIntervals; i++) {
    const startTime = new Date(
      startDateFormatted.getTime() +
        i * intervalLength +
        Math.floor((remainder * i) / numIntervals),
    );
    const endTime = new Date(
      startTime.getTime() +
        intervalLength +
        (i === numIntervals - 1 ? remainder % numIntervals : 0),
    );

    intervals.push({
      startDate: moment(new Date(startTime)).format("YYYY-MM-DD"),
      startTime: moment(new Date(startTime)).format("HH:mm"),
      endDate: moment(new Date(endTime)).format("YYYY-MM-DD"),
      endTime: moment(new Date(endTime)).format("HH:mm"),
    });
  }

  return intervals;
}

export const getTimeFormat = (time) => {
  if (/^\d{2}:\d{2}$/.test(time)) {
    return time;
  }
  return moment(new Date(time)).format("HH:mm");
};

export const getDateFormat = (date) => {
  return moment(new Date(date)).format("YYYY-MM-DD");
};

export function getWeekStartEndDate() {
  const today = new Date();

  // Get the day of the week (0: Sunday, 1: Monday, ..., 6: Saturday)
  const dayOfWeek = today.getDay();

  // Calculate the start of the week (Sunday)
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - dayOfWeek);

  // Calculate the end of the week (Saturday)
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return { startDate: weekStart, endDate: weekEnd };
}

export function getUpcoming7Dates() {
  const dates = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  return dates;
}

function getSlots(startDateISO, endDateISO, durationHours) {
  const slots = [];
  const durationMilliseconds = durationHours * 60 * 60 * 1000;
  let currentDate = new Date(startDateISO);

  const endDate = new Date(endDateISO);

  while (currentDate < endDate) {
    const nextSlot = new Date(currentDate.getTime() + durationMilliseconds);

    if (nextSlot > endDate) {
      break;
    }

    slots.push({
      startDate: getDateFormat(currentDate),
      startTime: getTimeFormat(currentDate),
      endDate: getDateFormat(nextSlot),
      endTime: getTimeFormat(nextSlot),
    });

    currentDate = nextSlot;
  }

  return slots;
}

export function getDateAndTimeList(
  strtDate,
  enDate,
  startTime,
  endTime,
  duration, //duration in hours
) {
  const [hr, min] = getTimeFormat(startTime)?.split(":");
  const [enHr, enMin] = getTimeFormat(endTime)?.split(":");
  const startDate = new Date(
    new Date(getDateFormat(strtDate)).setHours(hr, min),
  );
  const endDate = new Date(
    new Date(getDateFormat(enDate)).setHours(
      Number(enHr) + Number(duration),
      enMin,
    ),
  );

  let reqDates = [];

  while (startDate <= endDate) {
    const reqEndTime = new Date(
      new Date(getDateFormat(startDate)).setHours(
        Number(enHr) + Number(duration),
        enMin,
      ),
    );
    const times = getSlots(
      startDate.toISOString(),
      reqEndTime.toISOString(),
      duration,
    );
    reqDates = [...reqDates, ...times];
    startDate.setDate(startDate.getDate() + 1);
  }
  return reqDates;
}

export function validateTimeSlots(clinicHourList, duartion) {
  return clinicHourList?.every((day) => {
    return day?.slots?.every((slot) => {
      const startTime = new Date(slot?.startTime);
      const endTime = new Date(slot?.endTime);
      const timeDiff = getTimeDiff(endTime, startTime);
      return endTime > startTime && timeDiff?.mm >= Number(duartion);
    });
  });
}

export const getAllDatesFromArray = (list) => {
  const dates = [];
  list?.forEach((li) => {
    const startDate = new Date(li?.date);
    const endDate = new Date(li?.endDate);

    for (const d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
  });
  return uniq(dates);
};

export const getUpcomingSlotsWithoutBook = (slots) => {
  const currTime = moment(new Date()).format("HH:mm");
  return slots?.filter((item) => item?.time > currTime && item?.book !== "Y");
};

export const getTodayWithYMDFormat = () => {
  return moment(new Date()).format("YYYY-MM-DD");
};

export const getKey = () => {
  return (Math.random() + 1).toString(36).substring(7);
};

export function getHourRanges(times) {
  if (!times || times?.length === 0) return [];
  const uniqueHours = new Set(
    times?.map((time) => {
      const hour = time?.split(":")[0].padStart(2, "0");
      return `${hour}:00-${hour}:59`;
    }),
  );

  return Array.from(uniqueHours);
}

export function normalizeDate(dateStr) {
  if (!dateStr) return "";

  // If already in yyyy-mm-dd format, return as is
  const yyyymmddPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (yyyymmddPattern.test(dateStr)) {
    return dateStr;
  }

  // Detect delimiter
  const delimiter = dateStr.includes("-")
    ? "-"
    : dateStr.includes("/")
      ? "/"
      : null;
  if (!delimiter) return ""; // unsupported format

  const [day, month, yearPart] = dateStr.split(delimiter);
  const year = yearPart.length === 4 ? yearPart : `20${yearPart}`;

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

export const reduceYearsAndMonthsToStartOfMonth = ({
  years = 0,
  months = 0,
}) => {
  const currentDate = new Date();

  // Calculate the total reduction in terms of months
  const totalMonths = Math.floor(years * 12) + Math.floor(months);
  const remainingDays =
    (years * 12 - Math.floor(years * 12) + (months - Math.floor(months))) *
    30.44;

  const pastDate = new Date(currentDate);
  pastDate.setMonth(pastDate.getMonth() - totalMonths);
  pastDate.setDate(pastDate.getDate() - remainingDays);

  const startOfMonthDate = new Date(
    pastDate.getFullYear(),
    pastDate.getMonth(),
    1,
  );
  return moment(startOfMonthDate).format("YYYY-MM-DD");
};

export const getTreatmentRecordBasedType = (
  list = [],
  isBillCombined = false,
) => {
  return list
    ?.filter((det) => {
      const qtyMatch = det?.quantity > 0;
      const tabMatch =
        det?.billType === "administered" ||
        det?.billType === "treatment" ||
        det?.type === "consultation" ||
        det?.name === "Hospitalization";
      return qtyMatch && isBillCombined ? true : tabMatch;
    })
    ?.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
};

export const getPharmacyRecordBasedType = (
  list = [],
  isBillCombined = false,
) => {
  return list
    ?.filter((det) => {
      const qtyMatch = det?.quantity > 0;
      const tabMatch =
        det?.billType === "pharmacy" ||
        (det?.billType !== "treatment" &&
          det?.billType !== "administered" &&
          det?.type !== "consultation" &&
          det?.name !== "Hospitalization" &&
          det?.billType?.toLowerCase() !== "petshop");
      return qtyMatch && isBillCombined ? true : tabMatch;
    })
    ?.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
};

export const getPetShopRecordBasedType = (
  list = [],
  isBillCombined = false,
) => {
  return list
    ?.filter((det) => {
      const qtyMatch = det?.quantity > 0;
      const catMatch = det?.billType?.toLowerCase() === "petshop";
      return qtyMatch && (isBillCombined ? true : catMatch);
    })
    ?.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
};

export const getTaxAndSubTotalAndTotal = (list) => {
  let tax = 0,
    subTotal = 0,
    discount = 0,
    adminCharge = 0;
  tax = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.tax) ?? 0);
  }, 0);
  subTotal = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.total) ?? 0);
  }, 0);
  discount = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.discount) ?? 0) * (Number(obj?.quantity) ?? 0);
  }, 0);
  adminCharge = list?.reduce((acc, obj) => {
    return (
      acc +
      (obj?.adminCharge === "Y"
        ? Number(obj?.adminChargeAmount) * (Number(obj?.quantity) ?? 0)
        : 0)
    );
  }, 0);
  return {
    tax,
    subTotal,
    total: subTotal + tax + discount + adminCharge,
    discount,
    adminCharge,
  };
};

export const getTaxAndSubTotalAndTotalForTreatment = (
  list = [],
  services = [],
  procedures = [],
) => {
  let tax = 0,
    subTotal = 0,
    discount = 0,
    adminCharge = 0;
  list?.forEach((li) => {
    let reqList = [];
    if (li?.serviceId && li?.variantId && !li?.variantId) {
      const reqServ = services?.find((ser) => ser?.serviceId === li?.serviceId);
      const reqVarnt = reqServ?.serviceVariants?.find(
        (vnt) => vnt?.variantId === li?.variantId,
      );
      if (reqVarnt?.serviceDetails?.length > 0) {
        const filteredDetails =
          reqVarnt?.serviceDetails?.filter(
            (lit) =>
              (lit?.alterType === "N" && !lit?.alterName) ||
              (lit?.alterType === "Y" && !lit?.alterName),
          ) || [];

        filteredDetails.forEach((lit) => {
          // Check if item already exists to prevent duplicates
          const existingItem = reqList.find(
            (item) =>
              item?.serviceId === lit?.serviceId &&
              item?.variantId === lit?.variantId &&
              item?.name === lit?.name,
          );

          if (!existingItem) {
            reqList.push({
              ...lit,
              estCost: lit?.estimatedCost,
              serviceId: lit?.serviceId,
              variantId: lit?.variantId,
              serviceCharge: reqVarnt?.serviceCharge ?? 0,
            });
          }
        });
      }
      if (reqVarnt?.servicePrescreiptions?.length > 0) {
        const filteredPrescriptions =
          reqVarnt?.servicePrescreiptions?.filter(
            (lit) =>
              (lit?.alterType === "N" && !lit?.alterName) ||
              (lit?.alterType === "Y" && !lit?.alterName),
          ) || [];

        filteredPrescriptions?.forEach((lit, i) => {
          // Check if item already exists to prevent duplicates
          const existingItem = reqList?.find(
            (item) =>
              item?.serviceId === li?.serviceId &&
              item?.variantId === li?.variantId &&
              item?.name === lit?.name,
          );

          if (!existingItem) {
            const admnObj = {
              ...lit,
              estCost: lit?.estimatedCost ?? 0,
              serviceId: li?.serviceId,
              variantId: li?.variantId,
              serviceCharge: reqVarnt?.serviceCharge ?? 0,
            };
            reqList.push(admnObj);
          }
        });
      }

      reqList = reqList
        ?.map((lit) => ({
          ...lit,
          subItemName: lit?.name,
          serviceitemname: lit?.name,
          qty: lit?.quantity,
          unitPrice: lit?.estimatedCost ?? lit?.estCost,
          discount: 0,
          adminCharge: 0,
          total: lit?.estimatedCost ?? lit?.estCost,
        }))
        // Remove duplicates from reqList
        ?.filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (reqItem) =>
                reqItem?.subItemName === item?.subItemName &&
                reqItem?.serviceId === item?.serviceId &&
                reqItem?.variantId === item?.variantId,
            ),
        );
    }
    subTotal +=
      reqList?.length > 0
        ? Number(
            reqList
              ?.filter((rl) => (li?.variantId ? true : rl?.status === "true"))
              ?.reduce((acc, rl) => acc + Number(rl?.total), 0) +
              Number(reqList?.[0]?.serviceCharge ?? 0) -
              Number(li?.mrpDiscount ?? 0),
          )
        : li?.total;
  });

  tax = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.tax) ?? 0);
  }, 0);
  // subTotal = list?.reduce((acc, obj) => {
  //   return acc + (Number(obj?.total) ?? 0);
  // }, 0);
  discount = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.discount) ?? 0) * (Number(obj?.quantity) ?? 0);
  }, 0);
  adminCharge = list?.reduce((acc, obj) => {
    return (
      acc +
      (obj?.adminCharge === "Y"
        ? Number(obj?.adminChargeAmount) * (Number(obj?.quantity) ?? 0)
        : 0)
    );
  }, 0);
  return { tax, subTotal, total: subTotal + tax, discount, adminCharge };
};

export const getBillingAmount = (list) => {
  const treatRecords = getTreatmentRecordBasedType(list);
  const pharRecords = getPharmacyRecordBasedType(list);
  let subTotal = 0,
    tax = 0;
  if (treatRecords?.length > 0) {
    subTotal =
      subTotal +
      treatRecords?.reduce((acc, obj) => {
        return (
          acc + (Number(obj?.total ?? 0) + Number(obj?.adminChargeAmount ?? 0))
        );
      }, 0);
  }
  if (pharRecords?.length > 0) {
    subTotal =
      subTotal +
      pharRecords?.reduce((acc, obj) => {
        return acc + Number(obj?.price ?? 0);
      }, 0);
    tax =
      tax + pharRecords?.reduce((acc, obj) => acc + Number(obj?.tax ?? 0), 0);
  }

  return {
    subTotal,
    tax,
    total: subTotal + tax,
  };
};

export const convertStringToObject = (string) => {
  const obj = {};
  string.split("\n").forEach((line) => {
    const [key, value] = line.split(":").map((part) => part.trim());
    obj[key.charAt(0).toLowerCase() + key.slice(1)] =
      value === "Null" ? null : value;
  });
  return obj;
};

export const getExactNumber = (str) => {
  if (typeof str === "number") return str;
  let match = str?.match(/\d+(\.\d+)?/);
  return match ? match[0] : 0;
};

const getPercentageAsValue = (discount = 0, unitRate = 0) => {
  return (Number(unitRate) / 100) * Number(discount);
};

export const getTaxableValue = ({
  quantity = 1,
  unitRate = 0,
  discount = 0,
}) => {
  return Number(
    Number(quantity) *
      (Number(unitRate ?? 0) -
        Number(getPercentageAsValue(discount, unitRate))),
  );
};

export const getTaxValue = ({ taxableValue = 1, cgst = 0, sgst = 0 }) => {
  return Number((Number(taxableValue) / 100) * (Number(cgst) + Number(sgst)));
};

export const getTotalValues = ({ list = [], additionalCharges = 0 }) => {
  let taxableValues = 0;
  let taxes = 0;

  if (list?.length > 0) {
    taxableValues = list?.reduce(
      (acc, obj) => acc + Number(obj?.taxableValue),
      0,
    );
    taxes = list?.reduce((acc, obj) => acc + Number(obj?.taxes), 0);
  }

  return {
    taxableValues,
    taxes,
    grandTotal: taxableValues + taxes + Number(additionalCharges ?? 0),
  };
};

export const datePlusOneDay = new Date(
  new Date().setDate(new Date().getDate() + 1),
);

export const getTypeByAppointmentType = (typ) => {
  return typ === "scheduled"
    ? "Upcoming"
    : typ === "inprogress"
      ? "Checked-in"
      : typ === "intransit"
        ? "In Transit"
        : typ === "consultation"
          ? "Consultation"
          : typ === "finalize"
            ? "Billing"
            : typ === "completed"
              ? "Completed"
              : typ === "Pending"
                ? "Pending"
                : typ?.toLowerCase() === "canceled" ||
                    typ?.toLowerCase() === "cancelled"
                  ? "Cancelled"
                  : typ?.toLowerCase() === "noshow"
                    ? "No-show"
                    : "";
};

export function getDatesInRange(startDate, endDate) {
  const dateList = [];
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const day = ("0" + currentDate.getDate()).slice(-2);
    const month = ("0" + (currentDate.getMonth() + 1)).slice(-2);
    const year = currentDate.getFullYear();
    const formattedDate = new Date(`${year}-${month}-${day}`);
    dateList.push(formattedDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateList;
}

export const getStartAndEndDatesOfWeekByType = ({
  key = "next",
  weeks = 1,
}) => {
  if (key === "next") {
    const startDate = new Date();
    const endDate = new Date(new Date().setDate(new Date().getDate() + 6));
    startDate.setDate(new Date(startDate).getDate() + weeks * 7);
    endDate.setDate(new Date(endDate).getDate() + weeks * 7);
    return { startDate, endDate };
  }
  if (key === "prev") {
    // Calculate the end date (yesterday)
    let endDate = new Date();
    endDate.setDate(endDate.getDate() - 1); // Subtract 1 day to get yesterday's date

    // Get the exact start and end dates based on the weekNumber
    if (weeks > 1) {
      const daysToSubtract = (weeks - 1) * 7;
      endDate.setDate(endDate.getDate() - daysToSubtract);
    }

    // Calculate the start date (endDate - 6 days)
    let startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6); // Subtract 6 days from endDate

    return { startDate, endDate };
  }
};

export const getPriceWithoutTax = ({
  unitSellingPrice = 0,
  cgst = 0,
  sgst = 0,
}) => {
  const onePerPrice = Number(unitSellingPrice ?? 0) / 100;
  const tax =
    Number(onePerPrice ?? 0) * Number(Number(cgst ?? 0) + Number(sgst ?? 0));
  return {
    price: Number(Number(unitSellingPrice ?? 0) - Number(tax)) ?? 0,
    tax,
  };
};

export const getTotalEstimatedCost = (list = [], serCost = 0) => {
  if (list?.length === 0 || !list) return serCost;
  return (
    list?.reduce(
      (acc, obj) =>
        acc + (obj?.cost ?? obj?.estimatedCost ?? obj?.estCost ?? 0),
      0,
    ) + Number(serCost)
  );
};

export function splitTimeRange(startTime, endTime, slotDuration) {
  let slots = [];
  let current = new Date(`1970-01-01T${startTime}:00`);
  let endDate = new Date(`1970-01-01T${endTime}:00`);

  while (current < endDate) {
    let slotEnd = new Date(current.getTime() + slotDuration * 60000);
    if (slotEnd > endDate) {
      break; // Exit the loop if the slot exceeds the end time
    }

    slots.push({
      startTime: moment(current).format("HH:mm"),
      endTime: moment(slotEnd).format("HH:mm"),
    });

    current = slotEnd;
  }

  return slots;
}

export function splitMultipleRanges(ranges = [], slotDuration, dateStr) {
  if (ranges?.length === 0 || !ranges) return [];
  let allSlots = [];
  if (dateStr === moment().format("YYYY-MM-DD")) {
    let now = moment();
    ranges.forEach((range) => {
      let rangeStart = moment(`1970-01-01T${range?.startTime}:00`);
      let rangeEnd = moment(`1970-01-01T${range?.endTime}:00`);

      // Adjust the start time if it's in the past
      if (rangeStart?.isBefore(now)) {
        rangeStart = now
          .clone()
          .add(slotDuration - (now.minute() % slotDuration), "minutes")
          .startOf("minute");
      }

      let slots = splitTimeRange(
        rangeStart?.format("HH:mm"),
        rangeEnd?.format("HH:mm"),
        slotDuration,
      );
      allSlots = allSlots.concat(slots);
    });
  } else {
    for (let range of ranges) {
      let slots = splitTimeRange(range.startTime, range.endTime, slotDuration);
      allSlots = allSlots.concat(slots);
    }
  }
  return allSlots;
}

function removeUnavailableSlots(slots, ...unavailableArrays) {
  return slots?.filter((slot) => {
    return !unavailableArrays?.some((unavailableSlots) =>
      unavailableSlots.some((unavailable) => {
        const startSlot = new Date(`1970-01-01T${slot.startTime}:00`);
        const endSlot = new Date(`1970-01-01T${slot.endTime}:00`);
        const startUnavailable = new Date(
          `1970-01-01T${unavailable.startTime}`,
        );
        const endUnavailable = new Date(`1970-01-01T${unavailable.endTime}`);

        return (
          (startSlot >= startUnavailable && startSlot < endUnavailable) ||
          (endSlot > startUnavailable && endSlot <= endUnavailable) ||
          (startSlot <= startUnavailable && endSlot >= endUnavailable)
        );
      }),
    );
  });
}

const getReqBookedAppointments = (list) => {
  return list
    ?.filter(
      (li) =>
        li?.appoinmentStartTime !== null && li?.appointmentEndTime !== null,
    )
    ?.map((li) => ({
      startTime: li?.appoinmentStartTime,
      endTime: li?.appointmentEndTime,
    }));
};

export function removeRepeatingTimes(slots) {
  const uniqueSlots = [];
  const slotSet = new Set();

  slots.forEach((slot) => {
    const key = `${slot.startTime}-${slot.endTime}`;
    if (!slotSet.has(key)) {
      slotSet.add(key);
      uniqueSlots.push(slot);
    }
  });

  return uniqueSlots;
}

export function filterHolidaysByDate(holidays, givenDate) {
  return holidays?.filter(
    (holiday) =>
      new Date(holiday.date) <= new Date(givenDate) &&
      new Date(holiday.endDate) >= new Date(givenDate),
  );
}

export const getRequiredSlotList = ({
  availability,
  date,
  isDuration = false,
  bookedApnts = [],
  holidays = [],
}) => {
  const day = moment(new Date(date)).format("dddd");
  const currDate = moment(new Date(date))?.format("YYYY-MM-DD");
  const holidaysOnly = holidays?.filter((hl) => hl?.type === "holliday");
  if (filterHolidaysByDate(holidaysOnly, currDate)?.length) return [];
  const unAvailTimesOfTheDay = holidays?.filter(
    (hl) => hl?.type !== "holliday" && hl?.date === currDate,
  );
  const matchedDay = availability?.find(
    (avail) => avail?.day?.toLowerCase() === day?.toLowerCase(),
  );
  if (isDuration) return matchedDay?.duration;
  //getting all slots from times Array
  const slotList = splitMultipleRanges(matchedDay?.times, 15, currDate);
  const selectedDateSlots = removeRepeatingTimes(slotList);
  const availableSlotsAfterBooking = removeUnavailableSlots(
    selectedDateSlots ?? [],
    getReqBookedAppointments(bookedApnts) ?? [],
    unAvailTimesOfTheDay ?? [],
  );
  return availableSlotsAfterBooking ?? [];
};

export function getMonthsDifference(dateString) {
  const today = new Date();
  const givenDate = new Date(dateString);
  // Calculate the difference in years and months
  const yearDifference = today?.getFullYear() - givenDate?.getFullYear();
  const monthDifference = today?.getMonth() - givenDate?.getMonth();
  // Total months difference
  const totalMonths = yearDifference * 12 + monthDifference;
  return totalMonths;
}

export function adjustTime(inputTime, minutes) {
  const [hours, mins] = inputTime?.split(":").map(Number);
  let time = new Date();
  time?.setHours(hours);
  time?.setMinutes(mins);
  // Adjust time by the specified minutes
  time?.setMinutes(time?.getMinutes() + minutes);
  return time?.toTimeString().slice(0, 5); // Return in HH:MM format
}

export function getAllSelectedSlots(array1, array2) {
  // Sort array2 based on startTime
  array2.sort(
    (a, b) =>
      new Date(`1970-01-01T${a.startTime}:00`) -
      new Date(`1970-01-01T${b.startTime}:00`),
  );

  // Get the range from the sorted array2
  const start = new Date(`1970-01-01T${array2[0].startTime}:00`);
  const end = new Date(`1970-01-01T${array2[array2.length - 1].endTime}:00`);

  // Filter array1 based on the range
  return array1.filter((slot) => {
    const slotStart = new Date(`1970-01-01T${slot.startTime}:00`);
    return slotStart >= start && slotStart < end;
  });
}

export const getDateDiffInDays = (
  startDate = new Date(),
  endDate = new Date(),
) => {
  const date1 = new Date(startDate);
  const date2 = new Date(endDate);

  // Calculate the difference in milliseconds
  const differenceInMs = date2.getTime() - date1.getTime();

  // Convert milliseconds to days
  const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);
  return Math.ceil(differenceInDays);
};

const dayMap = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const getUpcoming60Dates = (daysArray = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Ensure time consistency
  if (daysArray?.length === 0 || !daysArray) return [];
  const excludedDays = daysArray
    ?.filter((d) => !d.status)
    ?.map((d) => dayMap[d.day]);
  let upcomingDates = [];
  let currentDate = new Date(today);
  let endDate = new Date(today);
  endDate.setMonth(today.getMonth() + 2); // Limit to two months
  while (currentDate <= endDate) {
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 0, 0); // Reset time for consistency
    if (excludedDays.includes(currentDate.getDay())) {
      upcomingDates.push(moment(currentDate).format("YYYY-MM-DD")); // Format YYYY-MM-DD
    }
  }
  return upcomingDates;
};

export const getSelectedInvoicesBalanceDue = (
  list = [],
  advanceAmount = 0,
  services = [],
  procedures = [],
) => {
  let total = 0;
  total = list?.reduce((acc, bill) => {
    return (
      acc +
      Number(
        (Number(
          roundWithSignedDiff(
            getTaxAndSubTotalAndTotalForTreatment(
              getTreatmentRecordBasedType(bill?.paymentDetails),
              services,
              procedures,
            )?.subTotal,
          )?.rounded +
            roundWithSignedDiff(
              getTaxAndSubTotalAndTotal(
                getPharmacyRecordBasedType(bill?.paymentDetails),
              )?.subTotal,
            )?.rounded +
            roundWithSignedDiff(
              getTaxAndSubTotalAndTotal(
                getPetShopRecordBasedType(bill?.paymentDetails),
              )?.subTotal,
            )?.rounded,
        ) ?? 0) - Number(bill?.amtPaid ?? 0),
      )
    );
  }, 0);

  return (Number(total) ?? 0) - Number(advanceAmount ?? 0);
};

export const getRefundValues = (list = []) => {
  let subTotal = 0,
    tax = 0;

  subTotal = list?.reduce((acc, obj) => {
    const reqValue = Number(obj?.price) * Number(obj?.returnQuantity);
    return acc + (Number(reqValue) ?? 0);
  }, 0);
  tax = list?.reduce((acc, obj) => {
    return (
      acc +
      Number(
        (Number(
          Number(obj?.tax) /
            Number(obj?.quantity > 0 ? obj?.quantity : obj?.returnQuantity),
        ) ?? 0) * Number(obj?.returnQuantity) ?? 0,
      )
    );
  }, 0);

  return {
    total: Number(
      (isNaN(Number(subTotal)) ? 0 : Number(subTotal)) +
        (isNaN(Number(tax)) ? 0 : Number(tax)),
    ),
    subTotal,
    tax,
  };
};

export const getTaxValueByBatchAndTaxPer = (batch = null, qty = 1) => {
  let total = 0,
    tax = 0;
  if (!batch) return { tax, total };
  const taxPer = Number(batch?.cgst ?? 0) + Number(batch?.sgst ?? 0);
  tax = (Number(batch?.unitSellingPrice) / 100) * Number(taxPer);
  total = Number(batch?.unitSellingPrice) * Number(qty);
  return { tax, total };
};

const getReqStock = (stocks, stockId) => {
  return stocks?.data?.find((stc) => stc?.stockId === stockId);
};

const getBatchDetails = (stock, batchId = null) => {
  return stock?.stockBatch?.find((bat) => bat?.batchId === batchId);
};

export const getSupplierRefundValues = (list = [], stocks = {}) => {
  let subTotal = 0,
    taxes = 0,
    total = 0;
  list?.forEach((li) => {
    const reqStock = getReqStock(stocks, li?.stockId);
    const batc = getBatchDetails(reqStock, li?.batchNumber?.value);
    const taxPer = Number(batc?.cgst ?? 0) + Number(batc?.sgst ?? 0);
    const tax =
      (Number(batc?.unitSellingPrice ?? 0) / 100) * Number(taxPer ?? 0);
    const reqValue =
      Number(batc?.unitSellingPrice ?? 0) * Number(li?.returnQuantity ?? 0);
    const reqTax = Number(tax) * Number(li?.returnQuantity ?? 0);
    taxes = taxes + (Number(reqTax) ?? 0);
    total = total + (Number(reqValue) ?? 0);
  });
  subTotal = Number(total) - Number(taxes);
  return { subTotal, taxes, total };
};

export const getOnlyName = (str = "") => {
  const words = str?.split(" "); // Convert string to array
  const lastItem = words[words?.length - 1]; // Get the last element
  if (!isNaN(lastItem)) {
    words.pop(); // Remove last item if it's a number
  }
  return words.join(" ");
};

export const getPetSearchType = (str = "") => {
  if (str?.length === 0) return "";
  const strNumber = Number(str);
  const isNonNumber = isNaN(strNumber);
  let type = "mobile";
  if (str?.length > 0 && !isNonNumber) type = "patient";
  if (str?.length > 5 && !isNonNumber) type = "mobile";
  if (str?.length > 0 && isNonNumber) type = "name";
  return `&filter=${type}`;
};

export function getWeekOffset(clickedDateStr, startDateStr) {
  const currentWeekStart = new Date(startDateStr);
  const clickedDate = new Date(clickedDateStr);
  const msPerWeek = 7 * 24 * 60 * 60 * 1000; // Milliseconds in a week
  const offset = Math.floor((clickedDate - currentWeekStart) / msPerWeek);
  return {
    prev: offset < 0 ? Math.abs(offset) : 0,
    next: offset > 0 ? offset : 0,
  };
}

export function getWeekRange(dateString, offset = 0) {
  const inputDate = new Date(dateString);
  const dayOfWeek = inputDate.getDay(); // Get day index (0 for Sunday, 6 for Saturday)

  // Calculate start of the week (Sunday)
  const startDate = new Date(inputDate);
  startDate.setDate(inputDate.getDate() - dayOfWeek + offset * 7);

  // Calculate end of the week (Saturday)
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  // Format YYYY-MM-DD
  const formatDate = (date) => date.toISOString().split("T")[0];
  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
}

export function getAddedDate(date, duration = 1) {
  const reqDur = duration - 1;
  if (reqDur > 0) {
    const reqDate = new Date(date);
    return new Date(new Date(reqDate).setDate(reqDate.getDate() + reqDur));
  }
  return new Date(date);
}

export const getTotalEstCostForWorkbook = (
  procedures = [],
  consumList = [],
  adminList = [],
  diagList = [],
) => {
  let total = 0;
  if (procedures?.length > 0) {
    total =
      total +
      procedures?.reduce((acc, obj) => {
        return (
          acc +
          (Number(
            Number(obj?.variant?.serviceCharge ?? 0) *
              Number(obj?.quantity ?? 1),
          ) ?? 0)
        );
      }, 0);
  }
  if (consumList?.length > 0) {
    total =
      total +
      consumList
        ?.filter((li) => li?.status)
        ?.reduce((acc, obj) => {
          return acc + (Number(obj?.estCost ?? 0) ?? 0);
        }, 0);
  }
  if (adminList?.length > 0) {
    total =
      total +
      adminList
        ?.filter((li) => li?.status)
        ?.reduce((acc, obj) => {
          return acc + (Number(obj?.estCost ?? 0) ?? 0);
        }, 0);
  }
  if (diagList?.length > 0) {
    total =
      total +
      diagList?.reduce((acc, obj) => {
        return (
          acc +
          (Number(Number(obj?.total ?? 0) * Number(obj?.quantity ?? 1)) ?? 0)
        );
      }, 0);
  }
  return total;
};

export const extensionToMime = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  json: "application/json",
  zip: "application/zip",
  mp4: "video/mp4",
  mp3: "audio/mpeg",
};

// export async function urlToFile(url, fileName) {
//   const response = await fetch(url);
//   const blob = await response.blob();
//   const mimeType = blob.type || "application/octet-stream";

//   // Convert Blob to File (in browser-like environments)
//   const file = new File([blob], fileName, { type: mimeType });
//   return file;
// }

export async function urlToFile(url, fallbackName = "file") {
  const response = await fetch(url);
  const blob = await response.blob();

  // Try to extract filename from URL
  const urlParts = url.split("/");
  const lastPart = urlParts[urlParts.length - 1].split("?")[0];
  const nameFromUrl =
    lastPart || `${fallbackName}.${blob.type.split("/")[1] || "bin"}`;

  const mimeType = blob.type || "application/octet-stream";
  return new File([blob], nameFromUrl, { type: mimeType });
}

export function generate4DigitRandomNumber() {
  return Math.floor(1000 + Math.random() * 9000);
}

function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

// Check if two time ranges overlap
function isOverlapping(start1, end1, start2, end2) {
  return start1 < end2 && start2 < end1;
}

export const getUnAvailableGroomList = (
  facilitySlots = [],
  actualSlots = [],
) => {
  const unavailableRanges = facilitySlots?.flatMap((f) =>
    f?.clinicFacilitiesUnavaility?.map((ua) => ({
      start: timeToMinutes(ua?.appointmentStartTime),
      end: timeToMinutes(ua?.appointmentEndTime),
    })),
  );

  const availableSlots = actualSlots?.filter((slot) => {
    const slotStart = timeToMinutes(slot?.startTime);
    const slotEnd = timeToMinutes(slot?.endTime);

    // Return true if this slot does NOT overlap with any unavailable range
    return !unavailableRanges.some(
      (range) => slotStart < range?.end && slotEnd > range?.start,
    );
  });

  return availableSlots ?? [];
};

export const getOnlyAvailableSlots = (facilitySlots, actualSlots) => {
  const filteredSlots = actualSlots?.filter((slot) => {
    const slotStart = timeToMinutes(slot?.startTime);
    const slotEnd = timeToMinutes(slot?.endTime);

    // Check for overlap with any appointment
    const overlaps = facilitySlots?.some((appointment) => {
      const apptStart = timeToMinutes(appointment?.startTime);
      const apptEnd = timeToMinutes(appointment?.endTime);
      return isOverlapping(slotStart, slotEnd, apptStart, apptEnd);
    });

    return !overlaps;
  });
  return filteredSlots ?? [];
};

const isOverlappingForGroomer = (slotStart, slotEnd, rangeStart, rangeEnd) =>
  slotStart < rangeEnd && slotEnd > rangeStart;

const toMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export const getSlotsForGroomer = (facilities = [], actualSlots = []) => {
  const availableSlots = actualSlots?.filter(({ startTime, endTime }) => {
    const slotStart = toMinutes(startTime);
    const slotEnd = toMinutes(endTime);

    // Check all facilities
    return facilities?.some((facility) => {
      const overlaps = facility?.clinicFacilitiesUnavaility.some(
        ({ appointmentStartTime, appointmentEndTime }) =>
          isOverlappingForGroomer(
            slotStart,
            slotEnd,
            toMinutes(appointmentStartTime),
            toMinutes(appointmentEndTime),
          ),
      );
      return !overlaps; // true if this facility has no conflict
    });
  });
  return availableSlots ?? [];
};

export const getAvailableBayAtTime = (facilities = [], time) => {
  const checkTime = toMinutes(time);
  let candidates = [];

  for (const bay of facilities) {
    const unavailability = bay?.clinicFacilitiesUnavaility || [];

    // Sort slots by end time descending
    const sorted = [...unavailability].sort(
      (a, b) =>
        toMinutes(b?.appointmentEndTime) - toMinutes(a?.appointmentEndTime),
    );

    let latestEndTime = null;
    let isAvailable = true;

    for (const slot of sorted) {
      const start = toMinutes(slot?.appointmentStartTime);
      const end = toMinutes(slot?.appointmentEndTime);

      if (checkTime >= start && checkTime < end) {
        isAvailable = false; // overlaps
        break;
      }

      // Track latest end time before this time
      if (end <= checkTime) {
        latestEndTime = end;
        break;
      }
    }

    if (isAvailable) {
      candidates.push({
        ...bay,
        lastEndTime: latestEndTime ?? 0,
      });
    }
  }

  if (candidates.length === 0) return null;

  // Pick the bay that was last used most recently (max utilization)
  candidates.sort((a, b) => b.lastEndTime - a.lastEndTime);
  return {
    facilityId: candidates[0].facilityId,
    name: candidates[0].name,
  };
};

export const getClassNameByServiceType = (serviceType) => {
  if (serviceType?.toLowerCase() === "consultation")
    return "card-bot-rose-back";
  if (serviceType?.toLowerCase() === "grooming") return "card-bot-blue-back";
  if (
    serviceType?.toLowerCase() === "lab tests" ||
    serviceType?.toLowerCase() === "vaccination"
  )
    return "card-bot-green-back";
  if (serviceType?.toLowerCase() === "radiology tests")
    return "card-bot-yellow-back";
  if (serviceType?.toLowerCase() === "surgery") return "card-bot-orange-back";
  if (serviceType?.toLowerCase() === "others") return "card-bot-pink-back";
  return "card-bot-pink-back";
};

export const getClassNameForCalendar = (serviceType) => {
  if (serviceType?.toLowerCase() === "consultation") return "cal-purple-box";
  if (serviceType?.toLowerCase() === "grooming") return "cal-blue-box";
  if (["lab tests", "vaccination"]?.includes(serviceType?.toLowerCase()))
    return "cal-green-box";
  if (serviceType?.toLowerCase() === "radiology tests") return "cal-yellow-box";
  if (serviceType?.toLowerCase() === "surgery") return "cal-orange-box";
  if (serviceType?.toLowerCase() === "others") return "cal-pink-box";
  return "cal-purple-box";
};

const today = new Date().toISOString().split("T")[0];

const isAvailableToday = (startDate, endDate) => {
  if (!startDate && !endDate) return true;
  if (startDate && endDate) return today >= startDate && today <= endDate;
  if (startDate) return today >= startDate;
  if (endDate) return today <= endDate;
  return false;
};

export const getDiscountCodeAvailableToday = (
  discounts = [],
  userId = null,
  billAmount = 0,
  billQty = 0,
  newCustomer = false,
  hasDiscount = false,
  petType = null,
) => {
  return discounts?.filter((discount) => {
    const {
      startDate,
      endDate,
      purchaseType,
      minAmount,
      minQuantity,
      billingHistory,
      specificCustomers,
      limitUsagePerCustomer,
      maxPerCustomerUses,
      limitUsageTotal,
      maxTotalUses,
      cannotBeCombined,
      discountMethod,
    } = discount;
    if (discountMethod === "Automatic discount") return false;
    if (!isAvailableToday(startDate, endDate)) return false;
    if (purchaseType === "One-time" && billingHistory?.length > 0) {
      if (billingHistory?.some((hst) => hst?.userId === userId)) return false;
    }
    if (minAmount > 0 && billAmount < minAmount) return false;
    if (minQuantity > 0 && billQty < minQuantity) return false;
    if (specificCustomers?.length > 0) {
      if (specificCustomers?.includes("newCustomers")) {
        if (!newCustomer) return false;
      }
    }
    if (limitUsagePerCustomer && billingHistory?.length > 0) {
      const listSize = billingHistory?.filter((hst) => hst?.userId === userId);
      if (listSize?.length >= maxPerCustomerUses) return false;
    }
    if (limitUsageTotal && billingHistory?.length > 0) {
      if (maxTotalUses === 0) return false;
    }
    if (cannotBeCombined && hasDiscount) return false;
    if (
      (specificCustomers?.includes("Household") ||
        specificCustomers?.includes("Rescue")) &&
      !specificCustomers?.includes(petType)
    )
      return false;
    return true;
  });
};

export const getPrevDiscountValues = (discounts = []) => {
  const result = { percentage: 0, price: 0 };
  if (discounts?.length === 0) return result;
  discounts.forEach((item) => {
    const { discountType, discountValue } = item;
    if (discountType?.toLowerCase() === "percentage") {
      result.percentage += discountValue;
    } else {
      result.price += discountValue;
    }
  });
  return result;
};

const getBatch = (
  stockId,
  batchId = null,
  retStock = false,
  clinicStocks = [],
) => {
  if (!stockId) return null;
  const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
  if (retStock) return reqStock;
  if (!batchId) return null;
  return reqStock?.stockBatch?.find((bat) => bat?.batchId === batchId) ?? null;
};

export const getConsumAdminListByServIdAndVarntId = (
  servId = null,
  varntId = null,
  services = [],
  clinicStocks,
  pharmacyTaxId = null,
) => {
  const reqServ = services?.find((ser) => ser?.serviceId === servId);
  const reqVarnt = reqServ?.serviceVariants?.find(
    (vnt) => vnt?.variantId === varntId,
  );
  let reqConsums = [],
    reqAdmins = [],
    reqPres = [];
  if (reqVarnt?.serviceDetails?.length > 0) {
    reqVarnt?.serviceDetails
      ?.filter(
        (li) =>
          (li?.alterType === "N" && !li?.alterName) ||
          (li?.alterType === "Y" && !li?.alterName),
      )
      ?.forEach((li) => {
        const selStock = clinicStocks?.find(
          (stc) => stc?.stockId === li?.stockId,
        );
        const batch = selStock?.autoConsumed
          ? selStock?.stockBatch?.[0]
          : getBatch(
              li?.stockId ?? null,
              li?.batchId ?? null,
              false,
              clinicStocks,
            );
        reqConsums.push({
          ...li,
          estCost: li?.estimatedCost,
          delete: false,
          reason: null,
          isEdit: true,
          deleteable: false,
          status: selStock?.autoConsumed ? true : null,
          detailId: null,
          serviceId: servId,
          variantId: varntId,
          drugSchedule: selStock?.drugSchedule ?? null,
          hsnSacCode: selStock?.hsnSacCode ?? null,
          packageUom: batch?.salesUom ?? null,
          expiryDate: batch?.expiryDate ?? null,
          batchNo: batch?.batchNo ?? null,
          mrpPrice: selStock?.autoConsumed ? 0 : (li?.mrpPrice ?? 0),
          tax: selStock?.autoConsumed ? 0 : (li?.tax ?? 0),
          total: selStock?.autoConsumed ? 0 : (li?.total ?? 0),
          batchId: selStock?.autoConsumed ? 0 : (li?.batchId ?? null),
          billable: selStock?.billable,
          autoConsumed: selStock?.autoConsumed,
        });
      });
  }
  if (reqVarnt?.servicePrescreiptions?.length > 0) {
    reqVarnt?.servicePrescreiptions
      ?.filter(
        (li) =>
          li?.administered?.toLowerCase() === "administered" &&
          ((li?.alterType === "N" && !li?.alterName) ||
            (li?.alterType === "Y" && !li?.alterName)),
      )
      ?.forEach((li, i) => {
        const selStock = clinicStocks?.find(
          (stc) => stc?.stockId === li?.stockId,
        );
        const batch = selStock?.autoConsumed
          ? selStock?.stockBatch?.[0]
          : getBatch(
              li?.stockId ?? null,
              li?.batchId ?? null,
              false,
              clinicStocks,
            );
        const batchNumber = selStock?.autoConsumed
          ? { name: batch?.batchNo, value: batch?.batchId }
          : { name: batch?.batchNo, value: batch?.batchId };
        const admnObj = {
          ...li,
          reason: null,
          route: routeList?.find((rl) => rl?.value === li?.vaccineType),
          i,
          batchNumber,
          deleteable: false,
          delete: false,
          isEdit: true,
          estCost: selStock?.autoConsumed ? 0 : (li?.estimatedCost ?? 0),
          status: selStock?.autoConsumed ? true : null,
          prescriptionId: li?.prescriptionId,
          detailId: null,
          serviceId: servId,
          variantId: varntId,
          drugSchedule: selStock?.drugSchedule ?? null,
          hsnSacCode: selStock?.hsnSacCode ?? null,
          packageUom: batch?.salesUom ?? null,
          expiryDate: batch?.expiryDate ?? null,
          batchNo: batch?.batchNo ?? null,
          mrpPrice: selStock?.autoConsumed ? 0 : (li?.mrpPrice ?? 0),
          tax: selStock?.autoConsumed ? 0 : (li?.tax ?? 0),
          total: selStock?.autoConsumed ? 0 : (li?.total ?? 0),
          batchId: selStock?.autoConsumed ? 0 : (li?.batchId ?? null),
          billable: selStock?.billable,
          autoConsumed: selStock?.autoConsumed,
        };
        reqAdmins.push(admnObj);
      });

    reqVarnt?.servicePrescreiptions
      ?.filter((li) => li?.administered?.toLowerCase() === "prescribed")
      ?.forEach((pre) => {
        const selStock = clinicStocks?.find(
          (stc) => stc?.stockId === pre?.stockId,
        );
        const btchDet = selStock?.stockBatch?.[0];
        const reqFreqList = ["onceDaily"];
        const reqPackValue = pre?.stockId
          ? getRightPackageUom(btchDet?.packageUom, false, true) > 0
            ? getRightPackageUom(btchDet?.packageUom, false, true)
            : 1
          : 1;
        const feedFrequency = getFeedingFrequency(reqFreqList);
        const reqDose =
          Number(pre?.dosage?.length > 0 ? pre?.dosage : 1) *
            Number(feedFrequency > 0 ? feedFrequency : 1) *
            Number(pre?.duration?.length > 0 ? pre?.duration : 1) >
          0
            ? Number(pre?.duration?.length > 0 ? pre?.duration : 1)
            : 1;
        const reqQty =
          Math.ceil(Number(reqDose) / Number(reqPackValue ?? 0)) ?? 0;
        const reqObj = {
          administered: false,
          type: pre?.type ?? null,
          stockId: pre?.stockId,
          batchId: btchDet?.batchId ?? null,
          name: selStock?.tradeName ?? selStock?.name ?? pre?.name,
          dosage: Number(pre?.dosage) > 0 ? pre?.dosage : "1",
          duration: Number(pre?.duration?.length > 0 ? pre?.duration : 1),
          route: pre?.route ?? null,
          meal: pre?.meal,
          quantity: reqQty,
          subUnit: "Unit",
          morning:
            (reqFreqList?.length > 0 && reqFreqList?.includes("morning")) ??
            false,
          afternoon:
            (reqFreqList?.length > 0 && reqFreqList?.includes("afternoon")) ??
            false,
          night:
            (reqFreqList?.length > 0 && reqFreqList?.includes("night")) ??
            false,
          adminChargeAmount: 0,
          adminCharge: "N",
          percentage: false,
          delete: false,
          discontinued: "N",
          detailId: pre?.detailId ?? null,
          remark: pre?.remark ?? "",
          drugSchedule: selStock?.drugSchedule ?? null,
          hsnSacCode: selStock?.hsnSacCode ?? null,
          packageUom: btchDet?.salesUom ?? null,
          expiryDate: btchDet?.expiryDate ?? null,
          batchNo: btchDet?.batchNo ?? null,
        };
        const taxPercent =
          pharmacyTaxId?.length > 0
            ? (btchDet?.cgst ? Number(btchDet?.cgst) : 0) +
              (btchDet?.sgst ? Number(btchDet?.sgst) : 0)
            : 0;
        const reqPercent = 100 + Number(taxPercent);
        const unitPrice =
          (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) /
          (reqPercent / 100);
        const taxValue =
          Number(
            (btchDet?.unitSellingPrice
              ? Number(btchDet?.unitSellingPrice)
              : 0) - Number(unitPrice),
          ) ?? 0;
        reqObj.price = btchDet?.unitSellingPrice ?? 0;
        reqObj.mrpPrice = btchDet?.unitSellingPrice
          ? Number(btchDet?.unitSellingPrice)
          : 0;
        reqObj.mrpDiscount = 0;
        reqObj.tax = Number(taxValue) * reqQty;
        reqObj.total =
          (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) *
            Number(reqQty) ?? 0;
        reqObj.frequencyDuration = reqFreqList ?? [];
        reqPres.push(reqObj);
        // const selStock = clinicStocks?.find(
        //   (stc) => stc?.stockId === pre?.stockId,
        // );
        // // const isNoAmount = selStock?.autoConsumed || !selStock?.billable;
        // const btchDet = selStock?.stockBatch?.[0];
        // const reqObj = {
        //   administered: false,
        //   type: pre?.type ?? null,
        //   stockId: pre?.stockId,
        //   batchId: pre?.batchId ?? null,
        //   name: pre?.name ?? "",
        //   dosage: pre?.dosage ?? "",
        //   duration: pre?.duration?.length > 0 ? pre?.duration : "1",
        //   meal: pre?.meal,
        //   quantity: pre?.quantity ?? 1,
        //   subUnit: "Unit",
        //   morning: pre?.morning ?? false,
        //   afternoon: pre?.afternoon ?? false,
        //   night: pre?.night ?? false,
        //   adminChargeAmount: 0,
        //   adminCharge: "N",
        //   percentage: false,
        //   delete: false,
        //   discontinued: "N",
        //   drugSchedule: selStock?.drugSchedule ?? null,
        //   hsnSacCode: selStock?.hsnSacCode ?? null,
        //   packageUom: btchDet?.salesUom ?? null,
        //   expiryDate: btchDet?.expiryDate ?? null,
        //   batchNo: btchDet?.batchNo ?? null,
        //   autoConsumed: selStock?.autoConsumed ?? false,
        // };
        // const taxPercent =
        //   pharmacyTaxId?.length > 0
        //     ? (btchDet?.cgst ? Number(btchDet?.cgst) : 0) +
        //       (btchDet?.sgst ? Number(btchDet?.sgst) : 0)
        //     : 0;
        // const reqPercent = 100 + Number(taxPercent);
        // const unitPrice =
        //   (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) /
        //   (reqPercent / 100);
        // const taxValue =
        //   Number(
        //     (btchDet?.unitSellingPrice
        //       ? Number(btchDet?.unitSellingPrice)
        //       : 0) - Number(unitPrice),
        //   ) ?? 0;
        // reqObj.price =
        //   //  isNoAmount ? 0 :
        //   unitPrice ?? 0;
        // reqObj.mrpPrice =
        //   //  isNoAmount? 0:
        //   btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0;
        // reqObj.mrpDiscount = 0;
        // // (Number(
        // //   btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0
        // // ) /
        // //   100) *
        // // Number(taxPercent);
        // reqObj.tax =
        //   //  isNoAmount ? 0 :
        //   Number(taxValue) *
        //   Number(typeof pre?.quantity === "undefined" ? 1 : pre?.quantity);
        // reqObj.total =
        //   // isNoAmount? 0:
        //   (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) *
        //     Number(typeof pre?.quantity === "undefined" ? 1 : pre?.quantity) ??
        //   0;
        // if (typeof pre?.frequencyDuration === "string") {
        //   reqObj.frequencyDuration = pre?.frequencyDuration;
        // }
        // if (pre?.productId) reqObj.productId = pre?.productId;
        // if (pre?.serviceId) reqObj.serviceId = pre?.serviceId;
        // reqPres.push(reqObj);
      });
  }
  return {
    consumList: reqConsums ?? [],
    adminList: reqAdmins ?? [],
    prescriptions: reqPres ?? [],
  };
};

const getFacility = (facilities = [], facilityId = null) => {
  return facilities?.find((fac) => fac?.facilityId === facilityId) ?? null;
};

const getLocationBatch = (
  clinicStocks = [],
  facilities = [],
  stockId = null,
  batchId = null,
  facilityId = null,
) => {
  const reqFacility =
    facilities?.length > 0 && facilityId
      ? getFacility(facilities, facilityId)
      : null;
  const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
  const currentBatchMatch = reqStock?.stockBatch?.find(
    (bat) =>
      bat?.batchId === batchId &&
      (bat?.locationId === reqFacility?.primaryStorage ||
        bat?.locationId === reqFacility?.secondaryStorage),
  );
  if (currentBatchMatch) return currentBatchMatch;
  const primaryLocationMatch = reqStock?.stockBatch?.filter(
    (bat) => bat?.locationId === reqFacility?.primaryStorage,
  );
  if (primaryLocationMatch?.length > 0) {
    return primaryLocationMatch?.[0];
  }
  const secondaryLocationMatch = reqStock?.stockBatch?.filter(
    (bat) => bat?.locationId === reqFacility?.secondaryStorage,
  );
  if (secondaryLocationMatch?.length > 0) {
    return secondaryLocationMatch?.[0];
  }
  if (localStorage.getItem("role") === "ROLE_CLINIC") {
    const otherLocationMatch = reqStock?.stockBatch?.filter(
      (bat) =>
        bat?.locationId !== reqFacility?.primaryStorage &&
        bat?.locationId !== reqFacility?.secondaryStorage,
    );
    return otherLocationMatch?.[0];
  }
  return {
    name: "Unknown",
    value: null,
    batchId: null,
    salesUom: "",
    subunits: null,
    quantity: null,
    locationId: null,
    locationName: "",
  };
};

export const getProcedureDetailsNew = async ({
  servId = null,
  varntId = null,
  serviceName = "",
  services = [],
  clinicStocks = [],
  pharmacyTaxId,
  status = null,
  facilities = [],
  facilityId = null,
  patientWorkbook = false,
  mainProcedure = false,
  locations = [],
}) => {
  const consumAdmnList = getConsumAdminListByServIdAndVarntId(
    servId,
    varntId,
    services,
    clinicStocks,
    pharmacyTaxId,
  );
  const reqServ = services?.find((ser) => ser?.serviceId === servId);
  const procedureDetails = [];

  // Process consumList items and wait for all async operations
  if (consumAdmnList?.consumList?.length > 0) {
    const consumListPromises = consumAdmnList?.consumList?.map(async (cl) => {
      const reqStock = clinicStocks?.find(
        (stc) => stc?.stockId === cl?.stockId,
      );
      const locatBat = await getLocationBatch(
        clinicStocks,
        facilities,
        cl?.stockId,
        cl?.batchId,
        facilityId,
      );
      const isNoAmount = reqStock?.autoConsumed || !reqStock?.billable;
      const reqBat = reqStock?.autoConsumed
        ? reqStock?.stockBatch?.[0]
        : cl?.batchId
          ? (reqStock?.stockBatch?.find(
              (bat) => bat?.batchId === cl?.batchId,
            ) ?? reqStock?.stockBatch?.[0])
          : reqStock?.stockBatch?.[0];
      const currLocat = locations?.find(
        (loc) => loc?.id === reqBat?.locationId,
      );
      const reqUnitPrice = 0;
      // reqBat?.unitSellingPrice ??
      // reqStock?.stockBatch?.[0]?.unitSellingPrice ??
      // 0;
      const taxPerent =
        reqServ?.taxApplicability?.toLowerCase() === "yes"
          ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
          : 0;
      const reqPercent = 100 + Number(taxPerent);
      const unitPrice = Number(reqUnitPrice) / (reqPercent / 100);
      const priceAfterDiscount = Number(unitPrice);
      const prdTaxValue = (taxPerent / 100) * Number(priceAfterDiscount);
      return {
        detailId: null,
        stockId: cl?.stockId ?? null,
        batchId: reqStock?.autoConsumed
          ? reqBat?.batchId
          : (locatBat?.batchId ?? null),
        type: "Consumables",
        quantity: Number(cl?.quantity ?? 1),
        status: reqStock?.autoConsumed
          ? true
          : locatBat?.batchId
            ? status
            : "requested",
        remarks: null,
        estimatedCost: isNoAmount
          ? 0
          : Number(reqUnitPrice) * Number(cl?.quantity ?? 1),
        name: reqStock?.productName ?? cl?.name,
        procedureName: reqStock?.productName ?? cl?.name,
        isDelete: false,
        serviceId: cl?.serviceId,
        tax: isNoAmount
          ? 0
          : Number(prdTaxValue ?? 0) * Number(cl?.quantity ?? 1),
        total: isNoAmount
          ? 0
          : Number(unitPrice ?? cl?.estimatedCost ?? cl?.estCost) *
            Number(cl?.quantity ?? 1),
        mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
        procedureVariantId: varntId,
        patientWorkbook,
        mainProcedure,
        drugSchedule: cl?.drugSchedule ?? null,
        hsnSacCode: cl?.hsnSacCode ?? null,
        packageUom: cl?.packageUom ?? null,
        expiryDate: cl?.expiryDate ?? null,
        batchNo: cl?.batchNo ?? null,
        billable: cl?.billable,
        autoConsumed: reqStock?.autoConsumed,
        billType: reqStock?.autoConsumed
          ? currLocat?.category
            ? billingTabEnum?.[currLocat?.category]
            : "treatment"
          : "treatment",
        linked: "Y",
      };
    });
    const consumListResults = await Promise.all(consumListPromises);
    procedureDetails.push(...consumListResults);
  }

  // Process adminList items and wait for all async operations
  if (consumAdmnList?.adminList?.length > 0) {
    const adminListPromises = consumAdmnList?.adminList?.map(async (al) => {
      const reqStock = clinicStocks?.find(
        (stc) => stc?.stockId === al?.stockId,
      );
      const locatBat = await getLocationBatch(
        clinicStocks,
        facilities,
        al?.stockId,
        al?.batchId,
        facilityId,
      );
      const isNoAmount = reqStock?.autoConsumed || !reqStock?.billable;
      const reqBat = reqStock?.autoConsumed
        ? reqStock?.stockBatch?.[0]
        : al?.batchId
          ? (reqStock?.stockBatch?.find(
              (bat) => bat?.batchId === al?.batchId,
            ) ?? reqStock?.stockBatch?.[0])
          : reqStock?.stockBatch?.[0];
      const currLocat = locations?.find(
        (loc) => loc?.id === reqBat?.locationId,
      );
      const reqUnitPrice = 0;
      // reqBat?.unitSellingPrice ??
      // reqStock?.stockBatch?.[0]?.unitSellingPrice ??
      // 0;
      const reqPack = reqBat?.packageUom?.match(/^(\d+)(.+)/);
      const priceForDosage =
        Number(reqUnitPrice ?? 0) / Number(reqPack?.[1] ?? 1);
      const taxPerent =
        reqServ?.taxApplicability?.toLowerCase() === "yes"
          ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
          : 0;
      const reqPercent = 100 + Number(taxPerent);
      const unitPrice = Number(priceForDosage) / (reqPercent / 100);
      const priceAfterDiscount = Number(unitPrice);
      const prdTaxValue = (taxPerent / 100) * Number(priceAfterDiscount);
      const reqUnitPriceForDosage = priceForDosage;
      return {
        detailId: null,
        stockId: al?.stockId ?? null,
        batchId: reqStock?.autoConsumed
          ? reqBat?.batchId
          : (locatBat?.batchId ?? null),
        type: "Administered",
        quantity: Number(al?.dosage ?? 1),
        status: reqStock?.autoConsumed
          ? true
          : locatBat?.batchId
            ? status
            : "requested",
        remarks: null,
        estimatedCost: isNoAmount
          ? 0
          : Number(reqUnitPriceForDosage ?? al?.estimatedCost ?? al?.estCost) *
            Number(al?.dosage ?? 1),
        name: reqStock?.productName ?? al?.name,
        procedureName: reqStock?.productName ?? al?.name,
        isDelete: false,
        serviceId: al?.serviceId,
        dosage: al?.dosage,
        route: al?.route?.value ?? null,
        tax: isNoAmount
          ? 0
          : Number(prdTaxValue ?? 0) * Number(al?.dosage ?? 1),
        total: isNoAmount
          ? 0
          : Number(reqUnitPriceForDosage ?? al?.estimatedCost ?? al?.estCost) *
            Number(al?.dosage ?? 1),
        mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
        procedureVariantId: varntId,
        patientWorkbook,
        mainProcedure,
        drugSchedule: al?.drugSchedule ?? null,
        hsnSacCode: al?.hsnSacCode ?? null,
        packageUom: al?.packageUom ?? null,
        expiryDate: al?.expiryDate ?? null,
        batchNo: al?.batchNo ?? null,
        billable: al?.billable,
        autoConsumed: reqStock?.autoConsumed,
        billType: reqStock?.autoConsumed
          ? currLocat?.category
            ? billingTabEnum?.[currLocat?.category]
            : "treatment"
          : "treatment",
        linked: "Y",
      };
    });
    const adminListResults = await Promise.all(adminListPromises);
    procedureDetails.push(...adminListResults);
  }
  return {
    procedureDetails,
    prescriptions:
      consumAdmnList?.prescriptions?.length > 0
        ? consumAdmnList?.prescriptions
        : [],
  };
};

export const getProcedureDetails = (
  servId = null,
  varntId = null,
  serviceName = "",
  services = [],
  clinicStocks = [],
  pharmacyTaxId,
  status = null,
) => {
  const consumAdmnList = getConsumAdminListByServIdAndVarntId(
    servId,
    varntId,
    services,
    clinicStocks,
    pharmacyTaxId,
  );
  const reqServ = services?.find((ser) => ser?.serviceId === servId);
  const procedureDetails = [];
  if (consumAdmnList?.consumList?.length > 0) {
    consumAdmnList?.consumList?.forEach((cl) => {
      const reqStock = clinicStocks?.find(
        (stc) => stc?.stockId === cl?.stockId,
      );
      const reqBat = cl?.batchId
        ? (reqStock?.stockBatch?.find((bat) => bat?.batchId === cl?.batchId) ??
          reqStock?.stockBatch?.[0])
        : reqStock?.stockBatch?.[0];
      const reqUnitPrice =
        reqBat?.unitSellingPrice ??
        reqStock?.stockBatch?.[0]?.unitSellingPrice ??
        0;
      const taxPerent =
        reqServ?.taxApplicability?.toLowerCase() === "yes"
          ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
          : 0;
      const reqPercent = 100 + Number(taxPerent);
      const unitPrice = Number(reqUnitPrice) / (reqPercent / 100);
      const priceAfterDiscount = Number(unitPrice);
      const prdTaxValue = (taxPerent / 100) * Number(priceAfterDiscount);
      const reqObj = {
        detailId: null,
        stockId: cl?.stockId ?? null,
        batchId: cl?.batchId ?? null,
        type: "Consumables",
        quantity: Number(cl?.quantity ?? 1),
        status: status,
        remarks: null,
        estimatedCost: cl?.estimatedCost ?? cl?.estCost,
        name: cl?.name,
        procedureName: cl?.name,
        isDelete: false,
        serviceId: cl?.serviceId,
        tax: Number(prdTaxValue ?? 0) * Number(cl?.quantity ?? 1),
        total:
          Number(cl?.estimatedCost ?? cl?.estCost) * Number(cl?.quantity ?? 1),
      };
      procedureDetails.push(reqObj);
    });
  }
  if (consumAdmnList?.adminList?.length > 0) {
    consumAdmnList?.adminList?.forEach((al) => {
      const reqStock = clinicStocks?.find(
        (stc) => stc?.stockId === al?.stockId,
      );
      const reqBat = al?.batchId
        ? (reqStock?.stockBatch?.find((bat) => bat?.batchId === al?.batchId) ??
          reqStock?.stockBatch?.[0])
        : reqStock?.stockBatch?.[0];
      const reqUnitPrice =
        reqBat?.unitSellingPrice ??
        reqStock?.stockBatch?.[0]?.unitSellingPrice ??
        0;
      const taxPerent =
        reqServ?.taxApplicability?.toLowerCase() === "yes"
          ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
          : 0;
      const reqPercent = 100 + Number(taxPerent);
      const unitPrice = Number(reqUnitPrice) / (reqPercent / 100);
      const priceAfterDiscount = Number(unitPrice);
      const prdTaxValue = (taxPerent / 100) * Number(priceAfterDiscount);
      const reqObj = {
        detailId: null,
        stockId: al?.stockId ?? null,
        batchId: al?.batchId ?? null,
        type: "Administered",
        quantity: Number(al?.quantity ?? 1),
        status: status,
        remarks: null,
        estimatedCost: al?.estimatedCost ?? al?.estCost,
        name: al?.name,
        procedureName: al?.name,
        isDelete: false,
        serviceId: al?.serviceId,
        dosage: al?.dosage,
        route: al?.route?.value ?? null,
        tax: Number(prdTaxValue ?? 0) * Number(al?.dosage ?? 1),
        total:
          Number(al?.estimatedCost ?? al?.estCost) *
          Number(al?.dosage ?? 1 ?? 1),
      };
      procedureDetails.push(reqObj);
    });
  }
  return {
    procedureDetails,
    prescriptions:
      consumAdmnList?.prescriptions?.length > 0
        ? consumAdmnList?.prescriptions
        : [],
  };
};

export function getDayOffsetNumber(dateStr) {
  const now = new Date();
  const target = new Date(dateStr);

  // Remove time for accurate day comparison
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = now - target; // positive if target is in the past
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays + 1;
}

export const getTimeSlotsBasedOnTasks = (list = []) => {
  const startTimes = list?.map((li) => li?.startTime?.split(":")[0]);
  const reqList = uniq(startTimes)
    ?.sort((a, b) => Number(a) - Number(b))
    ?.map((h) => `${String(h).padStart(2, "0")}:00`);
  return reqList;
};

// export const getTimeSlotsBasedOnTasks = (list) => {
//   const times = new Set();
//   list?.forEach((item) => {
//     const [startHour] = item?.startTime?.split(":").map(Number);

//     if (item?.endTime) {
//       const [endHour, endMinute] = item?.endTime?.split(":").map(Number);
//       const finalEndHour = endMinute > 0 ? endHour + 1 : endHour;

//       for (let h = startHour; h <= finalEndHour; h++) {
//         times.add(String(h).padStart(2, "0") + ":00");
//       }
//     } else {
//       times.add(String(startHour).padStart(2, "0") + ":00");
//     }
//   });
//   return Array.from(times).sort();
// };

export function getHourSlot(time) {
  const [hour] = time?.split(":")?.map(Number);
  return String(hour)?.padStart(2, "0") + ":00";
}

export const parseSoapNote = (noteString) => {
  try {
    // Clean the string first - remove extra spaces and ensure proper JSON format
    let cleanedString = noteString?.trim();

    // If it starts with a quote but no opening brace, add the opening brace
    if (cleanedString?.startsWith('"') && !cleanedString?.startsWith('{"')) {
      cleanedString = "{" + cleanedString;
    }

    // If it ends with a quote but no closing brace, add the closing brace
    if (cleanedString?.endsWith('"') && !cleanedString?.endsWith('"}')) {
      cleanedString = cleanedString + "}";
    }

    // Try to parse as JSON
    const jsonObject = JSON.parse(cleanedString);
    return jsonObject;
  } catch (error) {
    // If JSON parsing fails, try to fix common JSON issues
    try {
      let fixedString = noteString?.trim();

      // Remove leading/trailing quotes if they exist
      if (fixedString?.startsWith('"') && fixedString?.endsWith('"')) {
        fixedString = fixedString.slice(1, -1);
      }

      // Add braces if missing
      if (!fixedString.startsWith("{")) {
        fixedString = "{" + fixedString;
      }
      if (!fixedString.endsWith("}")) {
        fixedString = fixedString + "}";
      }

      const jsonObject = JSON.parse(fixedString);
      return jsonObject;
    } catch (secondError) {
      // If all JSON parsing fails, fall back to the original parsing logic
      const lines = noteString?.split("\n");
      const result = {};
      let currentKey = null;

      for (let line of lines) {
        line = line.trim();
        if (!line) continue;

        const keyMatch = line.match(/^([A-Za-z -]+):$/);
        if (keyMatch) {
          currentKey = keyMatch[1].replace(/-/g, "").replace(/\s+/g, "");
          result[currentKey] = "";
        } else if (currentKey) {
          if (result[currentKey]) {
            result[currentKey] += "\n" + line;
          } else {
            result[currentKey] = line;
          }
        }
      }

      return result;
    }
  }
};

export function filterRecordByHour(tasks, inputTime) {
  const inputHour = inputTime?.split(":")[0]; // e.g. "17"

  return tasks?.filter((task) => {
    if (!task?.startTime) return false;
    const taskHour = task?.startTime?.split(":")[0];
    return taskHour === inputHour; // match hour
  });
}

export const getActualTasks = (tasks = []) => {
  return tasks?.filter((task) => task?.taskType?.toLowerCase() !== "main");
};

const billingUserAccess = [
  "BillingHistory",
  "Billing",
  "Payments",
  "PointOfSale",
  "PaymentMaster",
  "PaymentDashboard",
  "Returns",
];
const role = localStorage.getItem("role");

export const getAccessForScreen = (screen = "") => {
  if (role === "ROLE_CLINIC") {
    return true;
  }
  if (screen === "") return;
  if (role === "ROLE_BILLING") {
    return billingUserAccess?.includes(screen);
  }
};

export function getTimeDiffInMins(time1, time2) {
  if (!time1 || !time2) return 0; // if any time missing

  const [h1, m1] = time1.split(":").map(Number);
  const [h2, m2] = time2.split(":").map(Number);

  if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return 0; // invalid input

  // Convert to total minutes
  const minutes1 = h1 * 60 + m1;
  const minutes2 = h2 * 60 + m2;

  // Get absolute difference
  const diff = Math.abs(minutes1 - minutes2);

  // Convert back to hours and minutes
  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  return { hours, minutes, totalMinutes: diff };
}

function flattenObject(obj) {
  return Object.entries(obj)
    ?.map(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        return `${key}: (${flattenObject(value)})`;
      }
      return `${key}: ${value}`;
    })
    ?.join(" | ");
}

export const getArryAsString = (arr) => {
  return arr?.length > 0
    ? arr?.map((doc) => flattenObject(doc)).join(" || ")
    : "";
};

export const getTodayDateMatchedRecords = (list = []) => {
  const formatDate = (dateStr) => new Date(dateStr).toISOString().split("T")[0];
  const result = list?.filter((li) => formatDate(li?.createdDate) === today);
  return result;
};

const KEYS = [
  "Summary",
  "Reminders",
  "InstructionsStatus",
  "AbnormalFindings",
  "KeyIssues",
  "RedFlags",
  "OtherUpdates",
  "Observation",
  "ProgressNotes",
  "Conclusion",
  "Protocols",
  "PreSurgicalDocumentation",
  "IntraSurgicalDocumentation",
  "PostSurgicalDocumentation",
  "OwnerCommunication",
  "AdditionalDocumentation",
];

export const processGroupedNotes = (notes = []) => {
  const grouped = notes?.reduce((result, note) => {
    const dateKey = note?.createdDate?.split("T")[0]; // YYYY-MM-DD only
    if (!result[dateKey]) result[dateKey] = [];
    result[dateKey].push(note);
    return result;
  }, {});

  // Consolidate & transform into string array
  return Object.values(grouped).flatMap((records) => {
    const consolidated = records?.find((r) => r?.consolidated);
    const finalRecords = consolidated ? [consolidated] : records;

    return finalRecords
      ?.map((note) => {
        const parts = [];
        KEYS.forEach((key) => {
          const reqKey = key.charAt(0).toLowerCase() + key.slice(1);
          if (note[reqKey]) {
            if (
              typeof note[reqKey] === "object" &&
              !Array.isArray(note[reqKey])
            ) {
              parts.push(`${key}: ${JSON.stringify(note[reqKey])}`);
            } else if (Array.isArray(note[reqKey])) {
              parts.push(`${key}: ${note[reqKey].join(", ")}`);
            } else {
              parts.push(`${key}: ${note[reqKey]}`);
            }
          }
        });
        return parts?.join(", ");
      })
      ?.filter(Boolean);
  });
};

export function toObject(data) {
  try {
    if (typeof data === "string") {
      return JSON.parse(data);
    }
    if (typeof data === "object" && data !== null) {
      return data;
    }
    throw new Error("Unsupported data type");
  } catch (err) {
    console.error("Error converting to object:", err);
    return null;
  }
}

export const transformData = (list = []) => {
  return list?.map((vnt) => {
    // Extract Consumables
    const consumables = (vnt?.consumList || [])
      ?.filter((c) => c?.delete === false)
      ?.map((c) => ({ name: c?.name, quantity: c?.quantity ?? 1 }));

    // Extract Administered
    const administered = (vnt?.presList || [])
      ?.filter((p) => p?.delete === false)
      ?.map((p) => ({
        name: p?.name,
        quantity: p?.quantity ?? 1,
        dosage: p?.dosage ?? "",
        route: p?.route?.value ?? null,
      }));

    // Extract prescriptions
    const prescriptions = (vnt?.prescriptions || [])
      ?.filter((pr) => pr?.delete === false)
      ?.map((pr) => ({
        name: pr?.name,
        quantity: pr?.quantity ?? 1,
        dosage: pr?.dosage ?? "",
        duration: pr?.duration,
        remarks: pr?.remarks,
        instructions: pr?.instructions?.value ?? null,
      }));

    // Build single string
    return `consumables:${JSON.stringify(
      consumables,
    )}, administered:${JSON.stringify(
      administered,
    )}, prescriptions:${JSON.stringify(prescriptions)}`;
  });
};

export const consolidateShouldBeTrue = (notes, type) => {
  const filtered = notes
    ?.filter((n) => n?.type === type) // filter by type
    ?.sort((a, b) => new Date(b?.createdDate) - new Date(a?.createdDate)); // sort desc
  if (!filtered?.length) return false;
  const latestConsolidated = filtered?.find((n) => n?.consolidated);
  // case: no consolidated → true only if at least 2 not consolidated exist
  if (!latestConsolidated) {
    return filtered?.filter((n) => !n?.consolidated)?.length >= 2;
  }
  // case: all consolidated → false
  if (filtered?.every((n) => n?.consolidated)) {
    return false;
  }
  // case: check if there is a !consolidated after latest consolidated
  const afterLatest = filtered?.filter(
    (n) =>
      !n?.consolidated &&
      new Date(n?.createdDate) > new Date(latestConsolidated?.createdDate),
  );
  if (afterLatest?.length) {
    // include older !consolidated also in counting
    const totalNotConsolidated = filtered?.filter(
      (n) => !n?.consolidated,
    )?.length;
    return totalNotConsolidated >= 2;
  }
  return false;
};

export const consolidateShouldBeTrueForOutpatient = (
  notes = [],
  apntId = null,
) => {
  const filtered = notes
    ?.filter((li) => li?.appointmentId === apntId)
    ?.sort((a, b) => new Date(b?.createdDate) - new Date(a?.createdDate)); // sort desc
  if (!filtered?.length) return false;
  const latestConsolidated = filtered?.find((n) => n?.consolidated);
  // case: no consolidated → true only if at least 2 not consolidated exist
  if (!latestConsolidated) {
    return filtered?.filter((n) => !n?.consolidated)?.length >= 2;
  }
  // case: all consolidated → false
  if (filtered?.every((n) => n?.consolidated)) {
    return false;
  }
  // case: check if there is a !consolidated after latest consolidated
  const afterLatest = filtered?.filter(
    (n) =>
      !n?.consolidated &&
      new Date(n?.createdDate) > new Date(latestConsolidated?.createdDate),
  );
  if (afterLatest?.length) {
    // include older !consolidated also in counting
    const totalNotConsolidated = filtered?.filter(
      (n) => !n?.consolidated,
    )?.length;
    return totalNotConsolidated >= 2;
  }
  return false;
};

const getReqService = (services, serviceId) => {
  return services?.find((li) => li?.serviceId === serviceId);
};

export const getPlanList = (planList = {}, services = [], appId = null) => {
  const result = [];

  Object.keys(planList)?.forEach((category) => {
    const items = planList?.[category];
    if (!Array.isArray(items)) return;

    items?.forEach((item) => {
      // If repeatTask is true AND noOfTimes > 1, use noOfTimes, else use 1
      const times =
        item?.repeatTask && Number(item?.noOfTimes) > 1
          ? Number(item?.noOfTimes)
          : 1;

      // If durationType is "Duration" AND duration > 1, use duration for splitting
      const shouldSplitByDuration =
        item?.durationType?.toLowerCase() === "duration" &&
        Number(item?.duration) > 1;
      const durationTimes = shouldSplitByDuration ? Number(item?.duration) : 1;

      if (item?.repeatTask && times > 1) {
        // convert start & end times into minutes
        const [sH, sM] = item?.startTime?.split(":").map(Number);
        const [eH, eM] = item?.endTime?.split(":").map(Number);
        const startTotal = sH * 60 + sM;
        const endTotal = eH * 60 + eM;

        // total duration
        const totalDuration =
          endTotal >= startTotal
            ? endTotal - startTotal
            : 24 * 60 - startTotal + endTotal;

        // If startTime and endTime are the same, use 1 hour as default slot duration
        const actualTotalDuration = totalDuration === 0 ? 60 : totalDuration;
        const slotDuration = actualTotalDuration / times;

        // Handle duration-based day repetition
        const daysToRepeat = shouldSplitByDuration ? durationTimes : 1;

        // Loop through days
        for (let dayOffset = 0; dayOffset < daysToRepeat; dayOffset++) {
          const baseDate = new Date(item?.startDate);
          baseDate.setDate(baseDate.getDate() + dayOffset);
          const currentDateStr = baseDate.toISOString().split("T")[0];

          // Loop through time slots within the day
          for (let i = 0; i < times; i++) {
            const currentStart = (startTotal + i * slotDuration) % (24 * 60);
            const currentEnd =
              (startTotal + (i + 1) * slotDuration) % (24 * 60);

            const formatTime = (mins) => {
              const h = String(Math.floor(mins / 60)).padStart(2, "0");
              const m = String(mins % 60).padStart(2, "0");
              return `${h}:${m}`;
            };

            let dateStr = currentDateStr;

            // Handle end date calculation - use same date as startDate unless time wraps past midnight
            const tempEndDate = new Date(dateStr);
            if (currentEnd < currentStart) {
              // If time wraps past midnight, increment end date
              tempEndDate.setDate(tempEndDate.getDate() + 1);
            }
            const endDateStr = tempEndDate.toISOString().split("T")[0];

            const reqObj = {
              category,
              task: item?.task,
              startDate: dateStr,
              startTime: formatTime(currentStart),
              repeatTask: item?.repeatTask,
              // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
              endDate: endDateStr,
              endTime: formatTime(currentEnd),
              times: item?.noOfTimes,
              stockId: item?.stockId ?? null,
              serviceId: item?.serviceId ?? null,
              variant:
                category?.toLowerCase() === "diagnostics"
                  ? (item?.variant ?? null)
                  : null,
              durationType: item?.durationType,
              duration: item?.duration,
              taskType: "subtask",
              active: item?.active,
            };
            if (category?.toLowerCase() === "diagnostics") {
              const reqSer = getReqService(services, item?.serviceId);
              const reqVarint =
                reqSer?.serviceVariants?.find(
                  (li) => li?.variantId === item?.variant,
                ) ?? reqSer?.serviceVariants?.[0];
              reqObj.appId = appId;
              reqObj.type = reqSer?.category;
              reqObj.testName = item?.task;
              reqObj.doneAt = doneAtList?.[0]?.value;
              reqObj.sampleCollected = false;
              reqObj.priority = "Routine";
              reqObj.price = reqVarint?.servicePrice;
              reqObj.tax = 0;
              reqObj.dueDate = moment(new Date(dateStr)).format("YYYY-MM-DD");
              reqObj.remark = "";
              reqObj.variantId = item?.variant;
              reqObj.variantName = reqVarint?.variantName;
              reqObj.addToBilling = false;
              reqObj.status = "Scheduled";
            }
            result.push(reqObj);
          }
        }
      } else if (shouldSplitByDuration) {
        // Handle duration-based splitting: repeat task based on duration
        for (let i = 0; i < durationTimes; i++) {
          const reqObj = {
            category,
            task: item?.task,
            startDate: item?.startDate,
            startTime: item?.startTime,
            repeatTask: item?.repeatTask,
            // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
            endDate: item?.endDate,
            endTime: item?.endTime,
            times: item?.noOfTimes,
            stockId: item?.stockId ?? null,
            serviceId: item?.serviceId ?? null,
            variant:
              category?.toLowerCase() === "diagnostics"
                ? (item?.variant ?? null)
                : null,
            durationType: item?.durationType,
            duration: item?.duration,
            taskType: "subtask",
            active: item?.active,
          };
          if (category?.toLowerCase() === "diagnostics") {
            const reqSer = getReqService(services, item?.serviceId);
            const reqVarint =
              reqSer?.serviceVariants?.find(
                (li) => li?.variantId === item?.variant,
              ) ?? reqSer?.serviceVariants?.[0];
            reqObj.appId = appId;
            reqObj.type = reqSer?.category;
            reqObj.testName = item?.task;
            reqObj.doneAt = doneAtList?.[0]?.value;
            reqObj.sampleCollected = false;
            reqObj.priority = "Routine";
            reqObj.price = reqVarint?.servicePrice;
            reqObj.tax = 0;
            reqObj.dueDate = moment(new Date(item?.startDate)).format(
              "YYYY-MM-DD",
            );
            reqObj.remark = "";
            reqObj.variantId = item?.variant;
            reqObj.variantName = reqVarint?.variantName;
            reqObj.addToBilling = false;
            reqObj.status = "Scheduled";
          }
          result.push(reqObj);
        }
      } else {
        // ✅ Handle non-repeat tasks (single entry)
        const reqObj = {
          category,
          task: item?.task,
          startDate: item?.startDate,
          startTime: item?.startTime,
          repeatTask: item?.repeatTask,
          // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
          endDate: item?.endDate,
          endTime: item?.endTime,
          times: item?.noOfTimes,
          stockId: item?.stockId ?? null,
          serviceId: item?.serviceId ?? null,
          variant:
            category?.toLowerCase() === "diagnostics"
              ? (item?.variant ?? null)
              : null,
          durationType: item?.durationType,
          duration: item?.duration,
          taskType: "subtask",
          active: item?.active,
        };
        if (category?.toLowerCase() === "diagnostics") {
          const reqSer = getReqService(services, item?.serviceId);
          const reqVarint =
            reqSer?.serviceVariants?.find(
              (li) => li?.variantId === item?.variant,
            ) ?? reqSer?.serviceVariants?.[0];
          reqObj.appId = appId;
          reqObj.type = reqSer?.category;
          reqObj.testName = item?.task;
          reqObj.doneAt = doneAtList?.[0]?.value;
          reqObj.sampleCollected = false;
          reqObj.priority = "Routine";
          reqObj.price = reqVarint?.servicePrice;
          reqObj.tax = 0;
          reqObj.dueDate = moment(new Date(item?.startDate)).format(
            "YYYY-MM-DD",
          );
          reqObj.remark = "";
          reqObj.variantId = item?.variant;
          reqObj.variantName = reqVarint?.variantName;
          reqObj.addToBilling = false;
          reqObj.status = "Scheduled";
        }
        result.push(reqObj);
      }
    });
  });

  return result;
};

// export const getPlanList = (planList = {}, services = [], appId = null) => {
//   const result = [];

//   Object.keys(planList)?.forEach((category) => {
//     const items = planList?.[category];
//     if (!Array.isArray(items)) return;

//     items?.forEach((item) => {
//       const times = Number(item?.noOfTimes) || 1;

//       if (item?.repeatTask) {
//         // convert start & end times into minutes
//         const [sH, sM] = item?.startTime?.split(":").map(Number);
//         const [eH, eM] = item?.endTime?.split(":").map(Number);
//         const startTotal = sH * 60 + sM;
//         const endTotal = eH * 60 + eM;

//         // total duration
//         const totalDuration =
//           endTotal >= startTotal
//             ? endTotal - startTotal
//             : 24 * 60 - startTotal + endTotal;

//         // duration per slot
//         const slotDuration = totalDuration / times;

//         for (let i = 0; i < times; i++) {
//           const currentStart = (startTotal + i * slotDuration) % (24 * 60);
//           const currentEnd = (startTotal + (i + 1) * slotDuration) % (24 * 60);

//           const formatTime = (mins) => {
//             const h = String(Math.floor(mins / 60)).padStart(2, "0");
//             const m = String(mins % 60).padStart(2, "0");
//             return `${h}:${m}`;
//           };

//           // ✅ if startDate == endDate → keep same date
//           // ✅ else → increment day when crossing midnight
//           let dateStr = item?.startDate;
//           let endDateStr = item?.endDate;

//           if (item?.startDate !== item?.endDate) {
//             // Handle multi-day case
//             const tempDate = new Date(item?.startDate);
//             tempDate.setDate(
//               tempDate.getDate() +
//                 Math.floor((startTotal + i * slotDuration) / (24 * 60))
//             );
//             dateStr = tempDate.toISOString().split("T")[0];

//             const tempEndDate = new Date(dateStr);
//             tempEndDate.setDate(
//               tempEndDate.getDate() + (currentEnd < currentStart ? 1 : 0) // if wrapped past midnight
//             );
//             endDateStr = tempEndDate.toISOString().split("T")[0];
//           }
//           const reqObj = {
//             category,
//             task: item?.task,
//             startDate: dateStr,
//             startTime: formatTime(currentStart),
//             repeatTask: item?.repeatTask,
//             // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
//             endDate: endDateStr,
//             endTime: formatTime(currentEnd),
//             times: item?.noOfTimes,
//             stockId: item?.stockId ?? null,
//             serviceId: item?.serviceId ?? null,
//             variant:
//               category?.toLowerCase() === "diagnostics"
//                 ? item?.variant ?? null
//                 : null,
//             durationType: item?.durationType,
//             duration: item?.duration,
//             taskType: "subtask",
//             active: item?.active,
//           };
//           if (category?.toLowerCase() === "diagnostics") {
//             const reqSer = getReqService(services, item?.serviceId);
//             const reqVarint =
//               reqSer?.serviceVariants?.find(
//                 (li) => li?.variantId === item?.variant
//               ) ?? reqSer?.serviceVariants?.[0];
//             reqObj.appId = appId;
//             reqObj.type = reqSer?.category;
//             reqObj.testName = item?.task;
//             reqObj.doneAt = doneAtList?.[0]?.value;
//             reqObj.sampleCollected = false;
//             reqObj.priority = "Routine";
//             reqObj.price = reqVarint?.servicePrice;
//             reqObj.tax = 0;
//             reqObj.dueDate = moment(new Date(dateStr)).format("YYYY-MM-DD");
//             reqObj.remark = "";
//             reqObj.variantId = item?.variant;
//             reqObj.variantName = reqVarint?.variantName;
//             reqObj.addToBilling = false;
//             reqObj.status = "Scheduled";
//           }
//           result.push(reqObj);
//         }
//       } else {
//         // ✅ Handle non-repeat tasks (single entry)
//         const reqObj = {
//           category,
//           task: item?.task,
//           startDate: item?.startDate,
//           startTime: item?.startTime,
//           repeatTask: item?.repeatTask,
//           // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
//           endDate: item?.endDate,
//           endTime: item?.endTime,
//           times: item?.noOfTimes,
//           stockId: item?.stockId ?? null,
//           serviceId: item?.serviceId ?? null,
//           variant:
//             category?.toLowerCase() === "diagnostics"
//               ? item?.variant ?? null
//               : null,
//           durationType: item?.durationType,
//           duration: item?.duration,
//           taskType: "subtask",
//           active: item?.active,
//         };
//         if (category?.toLowerCase() === "diagnostics") {
//           const reqSer = getReqService(services, item?.serviceId);
//           const reqVarint =
//             reqSer?.serviceVariants?.find(
//               (li) => li?.variantId === item?.variant
//             ) ?? reqSer?.serviceVariants?.[0];
//           reqObj.appId = appId;
//           reqObj.type = reqSer?.category;
//           reqObj.testName = item?.task;
//           reqObj.doneAt = doneAtList?.[0]?.value;
//           reqObj.sampleCollected = false;
//           reqObj.priority = "Routine";
//           reqObj.price = reqVarint?.servicePrice;
//           reqObj.tax = 0;
//           reqObj.dueDate = moment(new Date(item?.startDate)).format(
//             "YYYY-MM-DD"
//           );
//           reqObj.remark = "";
//           reqObj.variantId = item?.variant;
//           reqObj.variantName = reqVarint?.variantName;
//           reqObj.addToBilling = false;
//           reqObj.status = "Scheduled";
//         }
//         result.push(reqObj);
//       }
//     });
//   });

//   return result;
// };

export function reorderList(list) {
  const result = [];
  const used = new Set();

  // Find all main procedures first
  const mainProcedures = list?.filter(
    (li) =>
      // li?.patientWorkbook &&
      li?.mainProcedure,
  );

  mainProcedures?.forEach((mainItem, i) => {
    // push the mainProcedure first
    const reqProcedure = { ...mainItem, list: [], i };
    // find and push related items (same procedureId)
    const related = list?.filter(
      (li) =>
        // li?.patientWorkbook &&
        !li?.mainProcedure &&
        li?.procedureId === mainItem?.procedureId &&
        li?.linked !== "N",
    );
    if (related?.length > 0) {
      reqProcedure.list = related;
    }
    result.push(reqProcedure);
    used.add(mainItem?.paymentDetailId);
    related?.forEach((r) => {
      if (!used?.has(r?.paymentDetailId)) {
        result.push(r);
        used.add(r?.paymentDetailId);
      }
    });
  });

  // Add the remaining (not used) items
  list?.forEach((li) => {
    if (!used?.has(li?.paymentDetailId)) {
      result.push(li);
    }
  });
  return result;
}

export const checkIsVetRole = (vetList = [], apntVetId = null) => {
  if (!apntVetId || !vetList?.length || vetList?.length === 0) return false;
  const reqVet = vetList?.find((li) => li?.doctorId === apntVetId);
  const isVet = reqVet ? reqVet?.type?.toLowerCase() === "doctor" : false;
  // ? reqVet?.userType?.length > 0
  //   ? reqVet?.userType?.some((ut) => ut?.name === "ROLE_DOCTOR")
  //   : false
  // : false;
  return { isVet, saluation: reqVet?.salutation ?? null };
};

export const getRightSaluation = (vetList = [], vetId = null) => {
  if (!vetId || !vetList?.length || vetList?.length === 0) return "";
  const getVet = checkIsVetRole(vetList, vetId);
  if (getVet?.isVet) {
    return "Dr. ";
  }
  return `${getVet?.saluation ?? ""}${
    getVet?.saluation?.length > 0 ? ". " : ""
  }`;
};

export const getInpatNotes = (noteList = [], apntId = null) => {
  const formatDate = (dateStr) => new Date(dateStr).toISOString().split("T")[0];
  // Filter notes only for this appointment
  const notes =
    noteList?.filter(
      (cn) => cn?.appointmentId === apntId,
      //  &&
      //   cn?.type?.toLowerCase() === reqType,
    ) ?? [];

  // Group by date + type
  const grouped = notes?.reduce((acc, note) => {
    const d = formatDate(note?.createdDate);
    if (!acc[d]) acc[d] = { inpatient: [], surgery: [] };
    acc[d][note?.type || "inpatient"].push(note);
    return acc;
  }, {});

  // Process notes for each type
  const processDayNotes = (dayNotes) => {
    if (!dayNotes?.length) return [];

    // collect all consolidated notes for the day
    const consolidatedNotes = dayNotes?.filter((n) => n?.consolidated);

    if (consolidatedNotes?.length > 0) {
      // find the latest consolidated date
      const latestConsDate = new Date(
        Math.max(...consolidatedNotes.map((n) => new Date(n.createdDate))),
      );

      // return all consolidated + notes after the latest consolidated
      return [
        ...consolidatedNotes,
        ...dayNotes.filter((n) => new Date(n.createdDate) > latestConsDate),
      ];
    }

    // if no consolidated, return all
    return dayNotes;
  };

  // Flatten results
  const result = Object.values(grouped).flatMap(({ inpatient, surgery }) => [
    ...processDayNotes(inpatient),
    ...processDayNotes(surgery),
  ]);

  // Sort by createdDate descending
  return result?.sort(
    (a, b) => new Date(b?.createdDate) - new Date(a?.createdDate),
  );
};

export const getOutpatientNotes = (noteList = [], apntId = null) => {
  const filteredList = noteList?.filter((cn) => cn?.appointmentId === apntId);
  if (!Array.isArray(filteredList) || filteredList?.length === 0) return [];
  // Sort by createdDate (oldest → newest)
  const sorted = [...filteredList]?.sort(
    (a, b) => new Date(a?.createdDate) - new Date(b?.createdDate),
  );
  // Find consolidated records
  const consolidatedList = sorted?.filter((r) => r?.consolidated);
  let reqList = [];
  // Case 1: if no consolidated, return all
  if (consolidatedList?.length === 0) {
    reqList = sorted;
  } else {
    // Latest consolidated
    const latestConsolidated = consolidatedList?.[consolidatedList?.length - 1];
    const consolidatedDate = new Date(latestConsolidated?.createdDate);
    // Records after latest consolidated
    const afterConsolidated = sorted.filter(
      (r) => new Date(r?.createdDate) > consolidatedDate,
    );
    if (consolidatedList?.length === 1) {
      // Case 2 & 3
      reqList = [latestConsolidated, ...afterConsolidated];
    } else {
      // Case 4: More than one consolidated → return all consolidated + records after latest
      reqList = [...consolidatedList, ...afterConsolidated];
    }
  }

  // Always return newest first
  return reqList?.sort(
    (a, b) => new Date(b?.createdDate) - new Date(a?.createdDate),
  );
};

export const roundWithSignedDiff = (value) => {
  const rounded = Math.round(value);
  let diff = rounded - value;
  // remove negative sign for zero
  if (Object.is(diff, -0)) diff = 0;
  return {
    original: value,
    rounded,
    difference: diff?.toFixed(2),
  };
};

export const getRightPackageUom = (
  packageUom = "",
  getOnlyText = false,
  getOnlyNumber = false,
) => {
  if (!packageUom?.length || packageUom?.toLowerCase().includes("undefined")) {
    return "";
  }

  if (getOnlyNumber) {
    return packageUom?.match(/[0-9.]+/g)?.join("") || "";
  }

  if (getOnlyText) {
    return " " + packageUom?.replace(/[0-9.]/g, "");
  }

  return ` /${packageUom}`;
};

const shouldSkipTask = (dateStr, startTimeStr) => {
  if (!dateStr || !startTimeStr) {
    return false;
  }

  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];

  // Check if date is today
  if (dateStr !== todayStr) {
    return false;
  }

  // Compare current time with start time
  const [currentH, currentM] = [now.getHours(), now.getMinutes()];
  const [startH, startM] = startTimeStr.split(":").map(Number);

  const currentTotalMinutes = currentH * 60 + currentM;
  const startTotalMinutes = startH * 60 + startM;

  // Skip if current time is greater than start time
  return currentTotalMinutes > startTotalMinutes;
};

export const getPlanListNew = (planList = {}, services = [], appId = null) => {
  const result = [];

  Object.keys(planList)?.forEach((category) => {
    const items = planList?.[category];
    if (!Array.isArray(items)) return;
    items?.forEach((item) => {
      const currArr = [];
      // If repeatTask is true AND noOfTimes > 1, use noOfTimes, else use 1
      const times =
        item?.repeatTask && Number(item?.noOfTimes) > 1
          ? Number(item?.noOfTimes)
          : 1;

      // If durationType is "Duration" AND duration > 1, use duration for splitting
      const shouldSplitByDuration =
        item?.durationType?.toLowerCase() === "duration" &&
        Number(item?.duration) > 1;
      const durationTimes = shouldSplitByDuration ? Number(item?.duration) : 1;

      if (item?.repeatTask && times > 1) {
        // convert start & end times into minutes
        const [sH, sM] = item?.startTime?.split(":").map(Number);
        const [eH, eM] = item?.endTime?.split(":").map(Number);
        const startTotal = sH * 60 + sM;
        const endTotal = eH * 60 + eM;

        // total duration
        const totalDuration =
          endTotal >= startTotal
            ? endTotal - startTotal
            : 24 * 60 - startTotal + endTotal;

        // If startTime and endTime are the same, use 1 hour as default slot duration
        const actualTotalDuration = totalDuration === 0 ? 60 : totalDuration;
        const slotDuration = actualTotalDuration / times;

        // Handle duration-based day repetition
        const daysToRepeat = shouldSplitByDuration ? durationTimes : 1;

        // Loop through days
        for (let dayOffset = 0; dayOffset < daysToRepeat; dayOffset++) {
          const baseDate = new Date(item?.startDate);
          baseDate.setDate(baseDate.getDate() + dayOffset);
          const currentDateStr = baseDate.toISOString().split("T")[0];

          // Loop through time slots within the day
          for (let i = 0; i < times; i++) {
            const currentStart = (startTotal + i * slotDuration) % (24 * 60);
            const currentEnd =
              (startTotal + (i + 1) * slotDuration) % (24 * 60);

            const formatTime = (mins) => {
              const h = String(Math.floor(mins / 60)).padStart(2, "0");
              const m = String(mins % 60).padStart(2, "0");
              return `${h}:${m}`;
            };

            let dateStr = currentDateStr;

            // Handle end date calculation - use same date as startDate unless time wraps past midnight
            const tempEndDate = new Date(dateStr);
            if (currentEnd < currentStart) {
              // If time wraps past midnight, increment end date
              tempEndDate.setDate(tempEndDate.getDate() + 1);
            }
            const endDateStr = tempEndDate.toISOString().split("T")[0];

            const reqObj = {
              category,
              task: item?.task,
              startDate: dateStr,
              startTime: formatTime(currentStart),
              repeatTask: item?.repeatTask,
              // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
              endDate: endDateStr,
              endTime: formatTime(currentEnd),
              times: item?.noOfTimes,
              stockId: item?.stockId ?? null,
              serviceId: item?.serviceId ?? null,
              variant:
                category?.toLowerCase() === "diagnostics"
                  ? (item?.variant ?? null)
                  : null,
              durationType: item?.durationType,
              duration: item?.duration,
              taskType: "subtask",
              active: item?.active,
            };
            if (category?.toLowerCase() === "diagnostics") {
              const reqSer = getReqService(services, item?.serviceId);
              const reqVarint =
                reqSer?.serviceVariants?.find(
                  (li) => li?.variantId === item?.variant,
                ) ?? reqSer?.serviceVariants?.[0];
              reqObj.appId = appId;
              reqObj.type = reqSer?.category;
              reqObj.testName = item?.task;
              reqObj.doneAt = doneAtList?.[0]?.value;
              reqObj.sampleCollected = false;
              reqObj.priority = "Routine";
              reqObj.price = reqVarint?.servicePrice;
              reqObj.tax = 0;
              reqObj.dueDate = moment(new Date(dateStr)).format("YYYY-MM-DD");
              reqObj.remark = "";
              reqObj.variantId = item?.variant;
              reqObj.variantName = reqVarint?.variantName;
              reqObj.addToBilling = false;
              reqObj.status = "Scheduled";
            }
            // Skip if date is today and current time is greater than start time
            if (!shouldSkipTask(dateStr, formatTime(currentStart))) {
              currArr.push(reqObj);
            }
            // result.push(reqObj);
          }
        }
      } else if (shouldSplitByDuration) {
        // Handle duration-based splitting: repeat task based on duration
        for (let i = 0; i < durationTimes; i++) {
          const reqObj = {
            category,
            task: item?.task,
            startDate: item?.startDate,
            startTime: item?.startTime,
            repeatTask: item?.repeatTask,
            // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
            endDate: item?.endDate,
            endTime: item?.endTime,
            times: item?.noOfTimes,
            stockId: item?.stockId ?? null,
            serviceId: item?.serviceId ?? null,
            variant:
              category?.toLowerCase() === "diagnostics"
                ? (item?.variant ?? null)
                : null,
            durationType: item?.durationType,
            duration: item?.duration,
            taskType: "subtask",
            active: item?.active,
          };
          if (category?.toLowerCase() === "diagnostics") {
            const reqSer = getReqService(services, item?.serviceId);
            const reqVarint =
              reqSer?.serviceVariants?.find(
                (li) => li?.variantId === item?.variant,
              ) ?? reqSer?.serviceVariants?.[0];
            reqObj.appId = appId;
            reqObj.type = reqSer?.category;
            reqObj.testName = item?.task;
            reqObj.doneAt = doneAtList?.[0]?.value;
            reqObj.sampleCollected = false;
            reqObj.priority = "Routine";
            reqObj.price = reqVarint?.servicePrice;
            reqObj.tax = 0;
            reqObj.dueDate = moment(new Date(item?.startDate)).format(
              "YYYY-MM-DD",
            );
            reqObj.remark = "";
            reqObj.variantId = item?.variant;
            reqObj.variantName = reqVarint?.variantName;
            reqObj.addToBilling = false;
            reqObj.status = "Scheduled";
          }
          // Skip if date is today and current time is greater than start time
          if (!shouldSkipTask(item?.startDate, item?.startTime)) {
            currArr.push(reqObj);
          }
          // result.push(reqObj);
        }
      } else {
        // ✅ Handle non-repeat tasks (single entry)
        const reqObj = {
          category,
          task: item?.task,
          startDate: item?.startDate,
          startTime: item?.startTime,
          repeatTask: item?.repeatTask,
          // doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
          endDate: item?.endDate,
          endTime: item?.endTime,
          times: item?.noOfTimes,
          stockId: item?.stockId ?? null,
          serviceId: item?.serviceId ?? null,
          variant:
            category?.toLowerCase() === "diagnostics"
              ? (item?.variant ?? null)
              : null,
          durationType: item?.durationType,
          duration: item?.duration,
          taskType: "subtask",
          active: item?.active,
        };
        if (category?.toLowerCase() === "diagnostics") {
          const reqSer = getReqService(services, item?.serviceId);
          const reqVarint =
            reqSer?.serviceVariants?.find(
              (li) => li?.variantId === item?.variant,
            ) ?? reqSer?.serviceVariants?.[0];
          reqObj.appId = appId;
          reqObj.type = reqSer?.category;
          reqObj.testName = item?.task;
          reqObj.doneAt = doneAtList?.[0]?.value;
          reqObj.sampleCollected = false;
          reqObj.priority = "Routine";
          reqObj.price = reqVarint?.servicePrice;
          reqObj.tax = 0;
          reqObj.dueDate = moment(new Date(item?.startDate)).format(
            "YYYY-MM-DD",
          );
          reqObj.remark = "";
          reqObj.variantId = item?.variant;
          reqObj.variantName = reqVarint?.variantName;
          reqObj.addToBilling = false;
          reqObj.status = "Scheduled";
        }
        // Skip if date is today and current time is greater than start time
        if (!shouldSkipTask(item?.startDate, item?.startTime)) {
          currArr.push(reqObj);
        }
        // result.push(reqObj);
      }
      result.push({ ...item, times: item?.noOfTimes, tasks: currArr });
    });
  });

  return result;
};

export function minutesToHours(totalMinutes = 0) {
  const reqMin = Number(totalMinutes)?.toFixed(0);
  const hours = Math.floor(reqMin / 60);
  const minutes = reqMin % 60;

  if (hours >= 1) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

const MONTH_INDEX = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

export const sortByMonthYearDesc = (data = []) => {
  if (!Array.isArray(data)) return [];
  return [...data]?.sort((a, b) => {
    const [monthA, yearA] = a?.month?.split(" ");
    const [monthB, yearB] = b?.month?.split(" ");

    return +yearB - +yearA || MONTH_INDEX[monthB] - MONTH_INDEX[monthA];
  });
};

export const getSessionData = () => {
  const mandateData = sessionStorage.getItem("mandateData");
  const reqData = mandateData ? JSON.parse(mandateData) : null;
  return {
    clinic: reqData?.clinic ?? {},
    services: reqData?.services ?? [],
    stocks: reqData?.stocks ?? [],
    vets: reqData?.vets ?? [],
    dataSharing: reqData?.dataSharing ?? {},
  };
};

const frequencyCountMap = {
  morning: 1,
  afternoon: 1,
  night: 1,
  onceDaily: 1,
  twiceDaily: 2,
  every12Hours: 2,
  threeTimesDaily: 3,
  every8Hours: 3,
  fourTimesDaily: 6,
  every4Hours: 6,
};

export const getFeedingFrequency = (list = []) => {
  if (list?.length === 0 || !list) return 0;
  const total = (list ?? [])?.reduce((sum, item) => {
    return sum + (frequencyCountMap[item] || 0);
  }, 0);
  return total;
};

export const normalizeReportHtml = (html) => {
  if (html == null || typeof html !== "string") return "";
  return html
    ?.replace(/&n\s*bsp;/gi, "&nbsp;")
    ?.replace(/&a\s*mp;/gi, "&amp;")
    ?.replace(/&l\s*t;/gi, "&lt;")
    ?.replace(/&g\s*t;/gi, "&gt;");
};

export const getPaymentStatus = (bill = {}, isBillCombined = false) => {
  const { type, status, pharmacyStatus, petShopCheckout } = bill;

  if (type === "Pharmacy") {
    if (pharmacyStatus === "paid" || status === "paid") return "paid";
    if (pharmacyStatus === "partiallypaid" || status === "partiallypaid")
      return "partiallypaid";
    return "unpaid";
  }

  if (type === "Pet Shop") {
    if (petShopCheckout === "paid" || status === "paid") return "paid";
    if (petShopCheckout === "partiallypaid" || status === "partiallypaid")
      return "partiallypaid";
    return "unpaid";
  }

  if (type === "Clinic") {
    const statuses = [status, pharmacyStatus, petShopCheckout].filter(Boolean);

    const allPaid = statuses.every((s) => s === "paid");
    const anyPaidOrPartial = statuses?.some(
      (s) => s === "paid" || s === "partiallypaid",
    );

    if (allPaid || (isBillCombined && status === "paid")) return "paid";
    if (anyPaidOrPartial) return "partiallypaid";
    return "unpaid";
  }

  return "unpaid";
};

export const removeDuplicateBracketText = (str = "") => {
  const match = str?.match(/^(.*?)\s*\((.*?)\)\s*$/);
  if (!match) return str;
  const mainText = match[1]?.trim();
  const bracketText = match[2]?.trim();
  return mainText === bracketText ? mainText : str;
};
